// QuestDBPanel.jsx - FIXED RESPONSIVE MARGINS
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
  ScatterChart, Scatter, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceArea,
  Treemap, FunnelChart, Funnel, LabelList, Sankey, ComposedChart
} from 'recharts';
import {
  Trash2, Copy, RefreshCw, Settings, Play, Clock, Database, AlertCircle,
  Move, Filter, ZoomIn, Maximize2, Minimize2, X as XIcon, ArrowUpRight, EyeOff, MoreVertical
} from 'lucide-react';
import { createPortal } from 'react-dom';
import PanelContextMenu, { ShowDataModal } from './PanelContextMenu';
import { COLORS } from './constants';
import questdbService from '../../services/questdb';
import mysqlService from '../../services/mysql';
import realtimeService from '../../services/Realtimeservice';
import SimpleTransformations from './simpleTransformations';
import { parseAgentOffline } from '../../services/agentOffline.js';
import PlantOfflineBanner from '../common/PlantOfflineBanner.jsx';

function QuestDBPanel({ config, onEdit, onDelete, onDuplicate, onResize, style, darkMode, plantId,
  dashboardVariables = [], variableValues = {}, adhocFilters = [], readOnly = false,
  crossFilters = [], allActiveFilters = [], onEmitFilter, onClearCrossFilters,
  themePalette, onHidePanel }) {
  // Dashboard-level theme override — replaces the default COLORS palette
  // for every chart on this dashboard. Falls back to imported COLORS.
  const PANEL_COLORS = (Array.isArray(themePalette) && themePalette.length >= 3)
    ? themePalette : COLORS;
  // Focus mode (fullscreen expand) — local state; toggled by header button.
  const [focused, setFocused] = useState(false);
  // Mobile-view flag (viewport-based, not grid-width based) drives a compact
  // toolbar + trimmed footer on phones without touching desktop rendering.
  const [isMobileView, setIsMobileView] = useState(
    typeof window !== 'undefined' && window.innerWidth < 768
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const onResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // Right-click context menu + Show data modal
  const [ctxMenu, setCtxMenu] = useState(null);   // { x, y } or null
  const [showDataOpen, setShowDataOpen] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // The plant's agent is offline: an operational state, not a panel failure.
  // Held separately from `error` so the panel keeps its last data under a calm
  // amber banner instead of a red error. { plantId, since, lastDataTs } | null
  const [offline, setOffline] = useState(null);
  const [queryTime, setQueryTime] = useState(null);
  const [latestRecordTime, setLatestRecordTime] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [usingWebSocket, setUsingWebSocket] = useState(false);
  const timerRef = useRef(null);
  const subscriptionRef = useRef(null);

  // Zoom state
  const [zoomDomain, setZoomDomain] = useState(null);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const touchRef = useRef(null);
  const isSelectingRef = useRef(false);   // ref version avoids stale closure in mousemove
  const selectionStartRef = useRef(null); // ref version for mouseup finalization
  const zoomContainerRef = useRef(null);
  const wheelHandlerRef = useRef(null);
  const touchMoveHandlerRef = useRef(null);
  const zoomListenerCleanup = useRef(null);

  const theme = darkMode ? {
    card: '#1a1f35',
    hover: '#252b45',
    text: '#f8fafc',
    textSecondary: '#cbd5e1',
    textMuted: '#94a3b8',
    border: '#2d3548',
    chartGrid: 'rgba(148, 163, 184, 0.08)',
    chartAxis: '#94a3b8',
    chartText: '#94a3b8'
  } : {
    card: '#ffffff',
    hover: '#f3f4f6',
    text: '#0f172a',
    textSecondary: '#475569',
    textMuted: '#64748b',
    border: '#e2e8f0',
    chartGrid: 'rgba(0,0,0,0.06)',
    chartAxis: '#94a3b8',
    chartText: '#64748b'
  };

  // Apply "scenario adjustments" client-side: for each rule, multiply/add/etc
  // the named column by the current slider variable's value. Non-numeric cells
  // pass through unchanged. Missing sliders = no-op. Runs on every fetch, so
  // when the slider is dragged, the auto-refetch picks up the new value.
  const applyScenarios = (rows) => {
    const rules = Array.isArray(config.scenarioAdjustments) ? config.scenarioAdjustments : [];
    if (!rules.length || !Array.isArray(rows) || !rows.length) return rows;

    const resolveVarValue = (varName) => {
      const v = dashboardVariables.find(x => x.name === varName);
      if (!v) return undefined;
      let val = variableValues[v.id];
      if (val === undefined || val === null || val === '') val = v.defaultValue;
      if ((val === undefined || val === null || val === '') && v.type === 'slider') val = v.min ?? 0;
      const n = Number(val);
      return Number.isFinite(n) ? n : undefined;
    };

    return rows.map(row => {
      const next = { ...row };
      for (const rule of rules) {
        if (!rule || !rule.column || !rule.op || !rule.variableName) continue;
        const raw = Number(next[rule.column]);
        if (!Number.isFinite(raw)) continue;
        const factor = resolveVarValue(rule.variableName);
        if (factor === undefined) continue;
        switch (rule.op) {
          case '*':   next[rule.column] = raw * factor; break;
          case '+':   next[rule.column] = raw + factor; break;
          case '-':   next[rule.column] = raw - factor; break;
          case 'pct': next[rule.column] = raw * (1 + factor / 100); break;
          default: /* unknown op — skip */ break;
        }
      }
      return next;
    });
  };

  // Substitute $variableName / ${variableName} placeholders with current dashboard variable values.
  //
  // Notes:
  //   - `0` is a legitimate substitution value (common for what-if sliders),
  //     so we skip only on undefined / null / empty-string, NOT on falsy.
  //   - For slider variables specifically, if no user value is set yet, fall
  //     back to the configured `min` so the initial render doesn't send a
  //     literal `$name` to the database and error out.
  const resolveVariables = (query) => {
    if (!query || !dashboardVariables.length) return query;
    let resolved = query;
    dashboardVariables.forEach(variable => {
      let val = variableValues[variable.id];
      if (val === undefined || val === null || val === '') {
        val = variable.defaultValue;
      }
      if ((val === undefined || val === null || val === '') && variable.type === 'slider') {
        val = variable.min ?? 0;
      }
      if (val === undefined || val === null || val === '') return;
      const esc = variable.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      resolved = resolved
        .replace(new RegExp(`\\$\\{${esc}\\}`, 'g'), String(val))
        .replace(new RegExp(`\\$${esc}(?![a-zA-Z0-9_])`, 'g'), String(val));
    });
    return resolved;
  };

  // Build a query for one compare-series row. Inherits the panel's table,
  // timestampField, time-range, and limit; tacks on the series' own filters.
  const buildSeriesQuery = (s) => {
    if (!config.table) return '';
    const isMySQL = config.dbEngine === 'mysql';
    const tableName = isMySQL ? `\`${config.table}\`` : config.table;
    const whereClauses = [];

    // Time-range filter (same logic as base query)
    if (config.timeRange === 'last' && config.timeRangeLast && config.timestampField) {
      if (isMySQL) {
        const mysqlIntervals = {
          '5m': '5 MINUTE', '15m': '15 MINUTE', '1h': '1 HOUR',
          '6h': '6 HOUR', '24h': '24 HOUR', '7d': '7 DAY', '30d': '30 DAY'
        };
        const interval = mysqlIntervals[config.timeRangeLast];
        if (interval) whereClauses.push(`\`${config.timestampField}\` >= NOW() - INTERVAL ${interval}`);
      } else {
        const m = { '5m': ['m', 5], '15m': ['m', 15], '1h': ['h', 1], '6h': ['h', 6], '24h': ['h', 24], '7d': ['d', 7], '30d': ['d', 30] };
        const spec = m[config.timeRangeLast];
        if (spec) whereClauses.push(`${config.timestampField} >= dateadd('${spec[0]}', -${spec[1]}, now())`);
      }
    } else if (config.timeRange === 'custom' && config.timeRangeStart && config.timeRangeEnd && config.timestampField) {
      whereClauses.push(isMySQL
        ? `\`${config.timestampField}\` BETWEEN '${config.timeRangeStart}:00' AND '${config.timeRangeEnd}:00'`
        : `${config.timestampField} BETWEEN cast('${config.timeRangeStart}:00' as timestamp) AND cast('${config.timeRangeEnd}:00' as timestamp)`);
    }

    // Series-specific filters
    for (const f of (s.filters || [])) {
      if (!f.field || !f.operator || f.value === '' || f.value == null) continue;
      const isNumber = !isNaN(parseFloat(f.value)) && isFinite(f.value);
      const v = isNumber ? f.value : `'${String(f.value).replace(/'/g, "''")}'`;
      const fname = isMySQL ? `\`${f.field}\`` : f.field;
      whereClauses.push(`${fname} ${f.operator} ${v}`);
    }

    // Dashboard-level variables (column = value form) — same as base path
    if (dashboardVariables && dashboardVariables.length > 0) {
      dashboardVariables.forEach(variable => {
        const val = variableValues[variable.id] ?? variable.defaultValue ?? '';
        if (!val || !variable.column) return;
        const isNum = !isNaN(parseFloat(val)) && isFinite(val);
        const escapedVal = isNum ? val : `'${String(val).replace(/'/g, "''")}'`;
        const colName = isMySQL ? `\`${variable.column}\`` : variable.column;
        whereClauses.push(`${colName} = ${escapedVal}`);
      });
    }

    // Dashboard-level ad-hoc filters
    if (adhocFilters && adhocFilters.length > 0) {
      adhocFilters.forEach(filter => {
        if (!filter.column || !filter.operator || filter.value === '') return;
        const isNumber = !isNaN(parseFloat(filter.value)) && isFinite(filter.value);
        const value = isNumber ? filter.value : `'${String(filter.value).replace(/'/g, "''")}'`;
        const colName = isMySQL ? `\`${filter.column}\`` : filter.column;
        whereClauses.push(`${colName} ${filter.operator} ${value}`);
      });
    }

    let sql = `SELECT * FROM ${tableName}`;
    if (whereClauses.length) sql += ` WHERE ${whereClauses.join(' AND ')}`;
    if (config.timestampField) {
      const tsField = isMySQL ? `\`${config.timestampField}\`` : config.timestampField;
      sql += ` ORDER BY ${tsField} DESC`;
    }
    sql += ` LIMIT ${config.limit ?? 100}`;
    return sql;
  };

  const buildQueryWithFiltersAndTimeRange = () => {
    let query = config.query;
    const isMySQL = config.dbEngine === 'mysql';

    if (config.dataSource === 'table' && config.table) {
      const tableName = isMySQL ? `\`${config.table}\`` : config.table;
      query = `SELECT * FROM ${tableName}`;

      const whereClauses = [];

      if (config.timeRange === 'last' && config.timeRangeLast) {
        if (isMySQL) {
          const mysqlIntervals = {
            '5m': '5 MINUTE', '15m': '15 MINUTE', '1h': '1 HOUR',
            '6h': '6 HOUR', '24h': '24 HOUR', '7d': '7 DAY', '30d': '30 DAY'
          };
          const interval = mysqlIntervals[config.timeRangeLast];
          if (interval) {
            const tsField = `\`${config.timestampField}\``;
            whereClauses.push(`${tsField} >= NOW() - INTERVAL ${interval}`);
          }
        } else {
          const timeRangeMap = {
            '5m':  { unit: 'm', amount: 5 },
            '15m': { unit: 'm', amount: 15 },
            '1h':  { unit: 'h', amount: 1 },
            '6h':  { unit: 'h', amount: 6 },
            '24h': { unit: 'h', amount: 24 },
            '7d':  { unit: 'd', amount: 7 },
            '30d': { unit: 'd', amount: 30 }
          };
          const timeSpec = timeRangeMap[config.timeRangeLast];
          if (timeSpec) {
            const timeClause = `${config.timestampField} >= dateadd('${timeSpec.unit}', -${timeSpec.amount}, now())`;
            whereClauses.push(timeClause);
            console.log('⏰ Time range clause:', timeClause);
          }
        }
      } else if (config.timeRange === 'custom' && config.timeRangeStart && config.timeRangeEnd) {
        if (isMySQL) {
          whereClauses.push(
            `\`${config.timestampField}\` BETWEEN '${config.timeRangeStart}:00' AND '${config.timeRangeEnd}:00'`
          );
        } else {
          whereClauses.push(
            `${config.timestampField} BETWEEN cast('${config.timeRangeStart}:00' as timestamp) AND cast('${config.timeRangeEnd}:00' as timestamp)`
          );
        }
      }

      // Panel-level filters
      if (config.filters && config.filters.length > 0) {
        config.filters.forEach(filter => {
          if (filter.field && filter.operator && filter.value !== '') {
            const isNumber = !isNaN(parseFloat(filter.value)) && isFinite(filter.value);
            const value = isNumber ? filter.value : `'${filter.value.replace(/'/g, "''")}'`;
            const fieldName = isMySQL ? `\`${filter.field}\`` : filter.field;
            whereClauses.push(`${fieldName} ${filter.operator} ${value}`);
          }
        });
      }

      // Dashboard-level variable filters (table-source panels use column = value)
      if (dashboardVariables && dashboardVariables.length > 0) {
        dashboardVariables.forEach(variable => {
          const val = variableValues[variable.id] ?? variable.defaultValue ?? '';
          if (!val || !variable.column) return;
          const isNum = !isNaN(parseFloat(val)) && isFinite(val);
          const escapedVal = isNum ? val : `'${val.replace(/'/g, "''")}'`;
          const colName = isMySQL ? `\`${variable.column}\`` : variable.column;
          whereClauses.push(`${colName} = ${escapedVal}`);
        });
      }

      // Dashboard-level ad-hoc filters
      if (adhocFilters && adhocFilters.length > 0) {
        adhocFilters.forEach(filter => {
          if (filter.column && filter.operator && filter.value !== '') {
            const isNumber = !isNaN(parseFloat(filter.value)) && isFinite(filter.value);
            const value = isNumber ? filter.value : `'${filter.value.replace(/'/g, "''")}'`;
            const colName = isMySQL ? `\`${filter.column}\`` : filter.column;
            whereClauses.push(`${colName} ${filter.operator} ${value}`);
          }
        });
      }

      // Cross-panel filters — emitted by other panels' chart-mark clicks
      // AND by slicer panels, via the DashboardFilterProvider.
      // Handles =, !=, <, >, LIKE (raw value), IN (comma-joined values),
      // BETWEEN (two comma-joined values).
      if (crossFilters && crossFilters.length > 0) {
        const esc = (v) => `'${String(v).replace(/'/g, "''")}'`;
        const litOrNum = (v) => {
          const s = String(v);
          const isNum = s !== '' && !isNaN(parseFloat(s)) && isFinite(s);
          return isNum ? s : esc(s);
        };
        crossFilters.forEach(cf => {
          if (!cf.column || cf.value === '' || cf.value === undefined || cf.value === null) return;
          const op = (cf.operator || '=').toUpperCase();
          const colName = isMySQL ? `\`${cf.column}\`` : cf.column;
          if (op === 'IN' || op === 'NOT IN') {
            const parts = String(cf.value).split(',').map(x => x.trim()).filter(Boolean).map(litOrNum);
            if (parts.length) whereClauses.push(`${colName} ${op} (${parts.join(', ')})`);
          } else if (op === 'BETWEEN') {
            const [a, b] = String(cf.value).split(',').map(x => x.trim());
            if (a && b) whereClauses.push(`${colName} BETWEEN ${litOrNum(a)} AND ${litOrNum(b)}`);
          } else if (op === 'LIKE' || op === 'NOT LIKE') {
            whereClauses.push(`${colName} ${op} ${esc(cf.value)}`);
          } else {
            whereClauses.push(`${colName} ${op} ${litOrNum(cf.value)}`);
          }
        });
      }

      if (whereClauses.length > 0) {
        query += ` WHERE ${whereClauses.join(' AND ')}`;
      }

      // Always ORDER BY timestamp DESC so we fetch the LATEST N rows by
      // insertion order, regardless of mode. formatForChart reverses, so the
      // newest values end up on the RIGHT of the chart. In simple mode the
      // X-axis is labeled with xAxisFields; in timestamp mode it's the
      // timestamp itself.
      if (config.timestampField) {
        const tsField = isMySQL ? `\`${config.timestampField}\`` : config.timestampField;
        query += ` ORDER BY ${tsField} DESC`;
      }
      query += ` LIMIT ${config.limit ?? 100}`;
    }

    return resolveVariables(query);
  };

  // Cross-filter emitter — fires when a bar/pie slice is clicked. Only fires
  // in simple (categorical) X-axis mode; clicking a time-series point isn't
  // useful as a cross-filter. Reads the value out of the clicked mark's
  // payload using the panel's configured X column.
  const handleMarkClick = useCallback((entry) => {
    if (!onEmitFilter) return;
    if (config.xAxisMode !== 'simple') return;
    const col = Array.isArray(config.xAxisFields) && config.xAxisFields.length
      ? config.xAxisFields[0]
      : null;
    if (!col) return;
    const p = entry?.payload || entry || {};
    const val = p[col] ?? p._xLabel ?? p._time;
    if (val === undefined || val === null || val === '') return;
    onEmitFilter(col, val, '=');
  }, [config.xAxisMode, config.xAxisFields, onEmitFilter]);

  const formatNumber = (value) => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    
    const num = parseFloat(value);
    
    switch (config.yAxisNumberFormat) {
      case 'none':
        return value.toString();
        
      case 'number':
        if (config.yAxisUseCommas) {
          const formatted = num.toFixed(config.yAxisDecimals ?? 2);
          const parts = formatted.split('.');
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          return parts.join('.');
        }
        return num.toFixed(config.yAxisDecimals ?? 2);
        
      case 'percent':
        return `${(num).toFixed(config.yAxisDecimals ?? 2)}%`;
        
      case 'percent_decimal':
        return `${(num * 100).toFixed(config.yAxisDecimals ?? 2)}%`;
        
      case 'scientific':
        return num.toExponential(config.yAxisDecimals ?? 2);
        
      case 'bytes':
        const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
        let size = Math.abs(num);
        let unitIndex = 0;
        while (size >= 1024 && unitIndex < units.length - 1) {
          size /= 1024;
          unitIndex++;
        }
        return `${size.toFixed(config.yAxisDecimals ?? 2)} ${units[unitIndex]}`;
        
      case 'bits':
        const bitUnits = ['bit', 'Kbit', 'Mbit', 'Gbit', 'Tbit', 'Pbit'];
        let bits = Math.abs(num);
        let bitUnitIndex = 0;
        while (bits >= 1000 && bitUnitIndex < bitUnits.length - 1) {
          bits /= 1000;
          bitUnitIndex++;
        }
        return `${bits.toFixed(config.yAxisDecimals ?? 2)} ${bitUnits[bitUnitIndex]}`;
        
      case 'bps':
        const bpsUnits = ['bps', 'Kbps', 'Mbps', 'Gbps', 'Tbps'];
        let bps = Math.abs(num);
        let bpsUnitIndex = 0;
        while (bps >= 1000 && bpsUnitIndex < bpsUnits.length - 1) {
          bps /= 1000;
          bpsUnitIndex++;
        }
        return `${bps.toFixed(config.yAxisDecimals ?? 2)} ${bpsUnits[bpsUnitIndex]}`;
        
      case 'Bps':
        const BpsUnits = ['B/s', 'KB/s', 'MB/s', 'GB/s', 'TB/s'];
        let Bps = Math.abs(num);
        let BpsUnitIndex = 0;
        while (Bps >= 1024 && BpsUnitIndex < BpsUnits.length - 1) {
          Bps /= 1024;
          BpsUnitIndex++;
        }
        return `${Bps.toFixed(config.yAxisDecimals ?? 2)} ${BpsUnits[BpsUnitIndex]}`;
        
      case 'duration_ms':
        const totalSeconds = Math.floor(num / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
        if (minutes > 0) return `${minutes}m ${seconds}s`;
        return `${seconds}s`;
        
      case 'duration_s':
        const h = Math.floor(num / 3600);
        const m = Math.floor((num % 3600) / 60);
        const s = Math.floor(num % 60);
        if (h > 0) return `${h}h ${m}m ${s}s`;
        if (m > 0) return `${m}m ${s}s`;
        return `${s}s`;
        
      case 'currency_usd':
        return `$${num.toLocaleString('en-US', { minimumFractionDigits: config.yAxisDecimals ?? 2, maximumFractionDigits: config.yAxisDecimals ?? 2 })}`;
        
      case 'currency_eur':
        return `€${num.toLocaleString('en-US', { minimumFractionDigits: config.yAxisDecimals ?? 2, maximumFractionDigits: config.yAxisDecimals ?? 2 })}`;
        
      case 'currency_gbp':
        return `£${num.toLocaleString('en-US', { minimumFractionDigits: config.yAxisDecimals ?? 2, maximumFractionDigits: config.yAxisDecimals ?? 2 })}`;
        
      case 'currency_jpy':
        return `¥${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
        
      case 'currency_inr':
        return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: config.yAxisDecimals ?? 2, maximumFractionDigits: config.yAxisDecimals ?? 2 })}`;
        
      case 'custom':
        if (config.yAxisCustomFormat) {
          try {
            return config.yAxisCustomFormat.replace('{value}', num.toFixed(config.yAxisDecimals ?? 2));
          } catch (e) {
            return num.toFixed(config.yAxisDecimals ?? 2);
          }
        }
        return num.toFixed(config.yAxisDecimals ?? 2);
        
      default:
        return num.toFixed(config.yAxisDecimals ?? 2);
    }
  };

  const formatTickValue = (value) => {
    const formatted = formatNumber(value);
    
    if (config.yAxisUnit) {
      if (config.yAxisUnitPosition === 'prefix') {
        return `${config.yAxisUnit}${formatted}`;
      } else {
        return `${formatted}${config.yAxisUnit}`;
      }
    }
    
    return formatted;
  };

  // Merge N already-formatted series datasets by their `_time` key.
  // Each series produces rows like {_time, _timestamp, _value, <yField>: number}.
  // Output is one row per unique _time with one column per series id.
  const joinSeriesByTime = (seriesResults) => {
    const byTime = new Map();
    for (const { series, rows } of seriesResults) {
      const colKey = series.id;
      for (const r of rows) {
        const k = r._time;
        if (!byTime.has(k)) byTime.set(k, { _time: k, _timestamp: r._timestamp });
        const dest = byTime.get(k);
        const val = r[series.yField] ?? r._value;
        dest[colKey] = typeof val === 'number' ? val : (val == null ? null : Number(val));
      }
    }
    const out = [...byTime.values()];
    out.sort((a, b) => {
      const at = a._timestamp, bt = b._timestamp;
      if (at != null && bt != null) return new Date(at) - new Date(bt);
      return String(a._time).localeCompare(String(b._time));
    });
    return out;
  };

  const fetchData = async (silent = false) => {
    try {
      setError(null);
      setOffline(null);
      if (!silent) setLoading(true);
      const startTime = Date.now();
      const isMySQL = config.dbEngine === 'mysql';

      // ── Multi-series compare mode ──
      if (Array.isArray(config.series) && config.series.length > 0) {
        const runOne = async (s) => {
          const sql = buildSeriesQuery(s);
          if (!sql) return { series: s, rows: [] };
          const resolved = resolveVariables(sql);
          const raw = isMySQL
            ? await mysqlService.query(resolved, config.mysqlDatabase)
            : await questdbService.query(resolved, plantId);
          const rows = questdbService.formatForChart(
            raw,
            config.timestampField,
            config.timezone || 'UTC',
            config.xAxisMode || 'timestamp',
            s.yField || 'value',
            config.xAxisFields || null
          );
          return { series: s, rows };
        };
        const results = await Promise.all(config.series.map(runOne));
        const joined = joinSeriesByTime(results);
        setQueryTime(new Date(Date.now()));
        setData(joined);
        setIsInitialLoad(false);
        setLoading(false);
        return;
      }

      const query = buildQueryWithFiltersAndTimeRange();

      if (!query) {
        throw new Error('No query specified');
      }

      console.log('🚀 Executing query:', query, isMySQL ? `for db: ${config.mysqlDatabase}` : `for plant: ${plantId}`);
      const result = isMySQL
        ? await mysqlService.query(query, config.mysqlDatabase)
        : await questdbService.query(query, plantId);
      console.log('📊 Raw query result:', result);
      console.log('🌍 Using timezone:', config.timezone || 'UTC');
      
      const formatted = questdbService.formatForChart(
        result,
        config.timestampField,
        config.timezone || 'UTC',
        config.xAxisMode || 'timestamp',
        config.yAxis || 'value',
        config.xAxisFields || null
      );
      console.log('📈 Formatted data sample:', formatted.slice(0, 3));

      let finalData = formatted;
      if (config.transformations && config.transformations.length > 0) {
        console.log('🔄 Applying transformations:', config.transformations);
        finalData = SimpleTransformations.applyTransformations(formatted, config.transformations);
      }
      // Apply UI-declared what-if scenario adjustments (no SQL editing).
      finalData = applyScenarios(finalData);

      const endTime = Date.now();
      setQueryTime(new Date(endTime));

      // "Latest record time" only makes sense in timestamp mode. In simple
      // mode the X-axis isn't time, so don't render a stale clock overlay.
      if (config.xAxisMode !== 'simple' && result.length > 0 && result[0][config.timestampField]) {
        setLatestRecordTime(new Date(result[0][config.timestampField]));
      } else if (config.xAxisMode === 'simple') {
        setLatestRecordTime(null);
      }
      
      console.log('✅ Final data count:', finalData.length);
      setData(finalData);
      setIsInitialLoad(false);
      setLoading(false);
    } catch (err) {
      const agentOffline = parseAgentOffline(err);
      if (agentOffline) {
        // Not an error: the plant went quiet. Keep whatever we last rendered and
        // let the banner explain it.
        setOffline(agentOffline);
        setError(null);
      } else {
        console.error('❌ Error fetching data:', err);
        setOffline(null);
        setError(err.message);
      }
      setLoading(false);
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
    }
  };

  // Stable key: only re-run effect when query-affecting config values actually change
  const configKey = JSON.stringify({
    dbEngine: config.dbEngine,
    mysqlDatabase: config.mysqlDatabase,
    query: config.query,
    table: config.table,
    timestampField: config.timestampField,
    xAxisMode: config.xAxisMode,
    refreshInterval: config.refreshInterval,
    filters: config.filters,
    timeRange: config.timeRange,
    timeRangeLast: config.timeRangeLast,
    timeRangeStart: config.timeRangeStart,
    timeRangeEnd: config.timeRangeEnd,
    customTimeFrom: config.customTimeFrom,
    customTimeTo: config.customTimeTo,
    transformations: config.transformations,
    timezone: config.timezone,
    limit: config.limit,
    variableValues,
    adhocFilters,
    crossFilters,
    series: config.series,
    xAxisFields: config.xAxisFields,
  });

  useEffect(() => {
    // Cleanup previous subscription(s) and timer
    if (subscriptionRef.current) {
      const subs = Array.isArray(subscriptionRef.current) ? subscriptionRef.current : [subscriptionRef.current];
      subs.forEach(id => realtimeService.unsubscribe(id));
      subscriptionRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setIsInitialLoad(true);
    setLoading(true);

    const isMySQL = config.dbEngine === 'mysql';

    // ── Multi-series live updates: one WS sub per series, re-join on every update ──
    if (Array.isArray(config.series) && config.series.length > 0) {
      // Initial fetch via HTTP
      fetchData();

      const useWS = realtimeService.isReady() && config.refreshInterval > 0 && (
        isMySQL ? !!config.mysqlDatabase : !!plantId
      );
      if (useWS) {
        setUsingWebSocket(true);
        // Track latest per-series rows in a ref-like object so any series
        // update can trigger a re-join + setData with all series' latest rows.
        const latestRows = new Map();   // series.id -> rows[]
        const seriesById = new Map(config.series.map(s => [s.id, s]));

        const reJoinAndSet = () => {
          const acc = [];
          for (const [sid, rows] of latestRows.entries()) {
            const series = seriesById.get(sid);
            if (series) acc.push({ series, rows });
          }
          setData(joinSeriesByTime(acc));
          setQueryTime(new Date());
          setError(null);
          setOffline(null);
          setIsInitialLoad(false);
          setLoading(false);
        };

        subscriptionRef.current = config.series.map(s => {
          const sql = buildSeriesQuery(s);
          if (!sql) return null;
          return realtimeService.subscribe({
            sql: resolveVariables(sql),
            plantId,
            dbEngine: config.dbEngine || 'questdb',
            mysqlDatabase: config.mysqlDatabase || null,
            intervalMs: config.refreshInterval,
            onData: (result) => {
              try {
                const formatted = questdbService.formatData(result);
                const rows = questdbService.formatForChart(
                  formatted,
                  config.timestampField,
                  config.timezone || 'UTC',
                  config.xAxisMode || 'timestamp',
                  s.yField || 'value',
                  config.xAxisFields || null
                );
                latestRows.set(s.id, rows);
                reJoinAndSet();
              } catch (err) {
                console.error('[Panel] Series data processing error:', err);
              }
            },
            onError: (errorMsg) => console.error(`[Panel] Series ${s.name || s.id} sub error:`, errorMsg)
          });
        }).filter(Boolean);
      } else {
        setUsingWebSocket(false);
        if (config.refreshInterval > 0) {
          timerRef.current = setInterval(() => fetchData(true), config.refreshInterval);
        }
      }
      return () => {
        if (subscriptionRef.current) {
          const subs = Array.isArray(subscriptionRef.current) ? subscriptionRef.current : [subscriptionRef.current];
          subs.forEach(id => realtimeService.unsubscribe(id));
          subscriptionRef.current = null;
        }
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      };
    }

    const query = buildQueryWithFiltersAndTimeRange();
    if (!query) return;

    // WebSocket subscriptions: QuestDB needs plantId (agent); MySQL needs mysqlDatabase
    const useWS = realtimeService.isReady() && config.refreshInterval > 0 && (
      isMySQL ? !!config.mysqlDatabase : !!plantId
    );

    // Always do initial HTTP fetch for immediate data
    fetchData();

    if (useWS) {
      // ===== WebSocket Pub/Sub Mode =====
      setUsingWebSocket(true);

      subscriptionRef.current = realtimeService.subscribe({
        sql: query,
        plantId,
        dbEngine: config.dbEngine || 'questdb',
        mysqlDatabase: config.mysqlDatabase || null,
        intervalMs: config.refreshInterval,
        onData: (result) => {
          try {
            const formatted = questdbService.formatData(result);
            const chartData = questdbService.formatForChart(
              formatted,
              config.timestampField,
              config.timezone || 'UTC',
              config.xAxisMode || 'timestamp',
              config.yAxis || 'value',
              config.xAxisFields || null
            );
            let finalData = chartData;

            if (config.transformations && config.transformations.length > 0) {
              finalData = SimpleTransformations.applyTransformations(chartData, config.transformations);
            }
            finalData = applyScenarios(finalData);

            setQueryTime(new Date(result.timestamp));
            if (config.xAxisMode !== 'simple' && formatted.length > 0 && formatted[0][config.timestampField]) {
              setLatestRecordTime(new Date(formatted[0][config.timestampField]));
            } else if (config.xAxisMode === 'simple') {
              setLatestRecordTime(null);
            }

            setData(finalData);
            setError(null);
            setOffline(null);
            setIsInitialLoad(false);
            setLoading(false);
          } catch (err) {
            console.error('[Panel] Data processing error:', err);
            setError(err.message);
          }
        },
        onError: (errorMsg) => {
          // The subscription path forwards the server's message as a plain
          // string, so the offline case arrives as prose rather than a body.
          const agentOffline = parseAgentOffline(errorMsg);
          if (agentOffline) {
            setOffline(agentOffline);
            setError(null);
            return;
          }
          console.error('[Panel] Subscription error:', errorMsg);
          setOffline(null);
          setError(errorMsg);
        }
      });
    } else {
      // ===== HTTP Polling Fallback =====
      setUsingWebSocket(false);

      if (config.refreshInterval > 0) {
        timerRef.current = setInterval(() => fetchData(true), config.refreshInterval);
      }
    }

    return () => {
      if (subscriptionRef.current) {
        realtimeService.unsubscribe(subscriptionRef.current);
        subscriptionRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configKey, plantId]);

  // Non-passive wheel + touchmove listeners so preventDefault() actually works
  // (React's synthetic onWheel/onTouchMove are passive by default)
  // Callback ref — fires whenever the zoom container div mounts/unmounts.
  // This is critical: a plain useEffect([vizType]) misses the case where
  // the div appears AFTER the loading state clears.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setZoomRef = useCallback((node) => {
    // Tear down previous listeners
    if (zoomListenerCleanup.current) {
      zoomListenerCleanup.current();
      zoomListenerCleanup.current = null;
    }
    zoomContainerRef.current = node;
    if (!node) return;

    const onWheel = (e) => { wheelHandlerRef.current?.(e); };
    const onTouchMove = (e) => { touchMoveHandlerRef.current?.(e); };

    node.addEventListener('wheel', onWheel, { passive: false });
    node.addEventListener('touchmove', onTouchMove, { passive: false });

    zoomListenerCleanup.current = () => {
      node.removeEventListener('wheel', onWheel);
      node.removeEventListener('touchmove', onTouchMove);
    };
  }, []); // empty deps — stable function, uses refs internally

  const formatTimestamp = (date) => {
    if (!date) return 'N/A';
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getColor = (idx) => {
    // Panel-level per-series colour override wins over the dashboard theme.
    if (config.colors && config.colors.length > idx && config.colors[idx]) {
      return config.colors[idx];
    }
    return PANEL_COLORS[idx % PANEL_COLORS.length];
  };

  const getYAxisDomain = () => {
    console.log('📊 Y-axis config:', {
      scale: config.yAxisScale,
      min: config.yAxisMin,
      max: config.yAxisMax
    });
    
    if (config.yAxisScale === 'custom') {
      const min = config.yAxisMin !== '' ? parseFloat(config.yAxisMin) : 'auto';
      const max = config.yAxisMax !== '' ? parseFloat(config.yAxisMax) : 'auto';
      console.log('📊 Custom Y-axis domain:', [min, max]);
      return [min, max];
    }
    return ['auto', 'auto'];
  };

  const panelWidth = config.width || 4;
  const isSmall = panelWidth <= 2;
  const isMedium = panelWidth > 2 && panelWidth <= 4;
  const fontSize = isSmall ? 9 : isMedium ? 10 : 11;

  // Smart margin calculation based on actual data values
  const calculateSmartMargins = (data, yFields, vizType) => {
    if (!data || data.length === 0 || vizType === 'stat' || vizType === 'table') {
      return { left: 40, right: 20, top: 35, bottom: 50 };
    }

    // Calculate max Y-axis label width by finding longest formatted value
    let maxYValue = 0;
    let minYValue = 0;
    yFields.forEach(field => {
      const values = data.map(d => d[field] || 0);
      maxYValue = Math.max(maxYValue, ...values);
      minYValue = Math.min(minYValue, ...values);
    });
    
    // Format both min and max to find the longest
    const formattedMax = formatTickValue(maxYValue);
    const formattedMin = formatTickValue(minYValue);
    const longestYLabel = formattedMax.length > formattedMin.length ? formattedMax : formattedMin;
    
    // Ultra-tight character width estimation
    const estimatedCharWidth = isSmall ? 5 : isMedium ? 5.5 : 6;
    const yLabelWidth = Math.ceil(longestYLabel.length * estimatedCharWidth);
    
    // MINIMAL left margin: just enough for Y-axis labels
    const leftMargin = Math.max(25, Math.min(yLabelWidth + 8, 65));
    
    // Calculate X-axis needs - check if rotation is set or use default
    const rotationAngle = config.xAxisTickRotation ?? -45; // Default to -45
    const hasRotatedXTicks = Math.abs(rotationAngle) > 0;
    const sampleXLabels = data.slice(0, Math.min(10, data.length)).map(d => d._time || '');
    const maxXLabelLength = Math.max(...sampleXLabels.map(l => String(l).length));
    
    // Minimal right margin
    const rightMargin = hasRotatedXTicks 
      ? Math.max(15, Math.min(maxXLabelLength * 1.5, 35))
      : Math.max(20, Math.min(maxXLabelLength * 2, 45));
    
    // Smart bottom margin based on rotation
    const bottomMargin = hasRotatedXTicks ? 80 : 40;
    
    return {
      left: leftMargin,
      right: rightMargin,
      top: 35,
      bottom: bottomMargin
    };
  };

  const renderChart = () => {
    if (loading && data.length === 0 && isInitialLoad) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <RefreshCw size={32} color="#667eea" style={{ animation: 'spin 1s linear infinite' }} />
            <p style={{ color: '#6b7280', marginTop: '12px', fontSize: '13px' }}>Loading data...</p>
          </div>
        </div>
      );
    }

    if (error && data.length === 0 && isInitialLoad) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '20px' }}>
          <div style={{
            padding: '16px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            color: '#ef4444',
            textAlign: 'center'
          }}>
            <AlertCircle size={32} style={{ marginBottom: '8px' }} />
            <div style={{ fontSize: '14px', fontWeight: '600' }}>Error loading data</div>
            <div style={{ fontSize: '12px', marginTop: '4px', opacity: 0.8 }}>{error}</div>
          </div>
        </div>
      );
    }

    if (data.length === 0) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <div style={{ textAlign: 'center', color: '#6b7280' }}>
            <Database size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
            <p style={{ fontSize: '13px' }}>No data available</p>
          </div>
        </div>
      );
    }

    // In multi-series mode, each series.id becomes its own dataKey on the
    // joined rows; the series's `color` and `name` drive stroke + legend.
    const seriesMode = Array.isArray(config.series) && config.series.length > 0;
    const yFields = seriesMode
      ? config.series.map(s => s.id)
      : ((config.yAxes && config.yAxes.length > 0) ? config.yAxes : [config.yAxis].filter(Boolean));
    const filteredYFields = seriesMode
      ? yFields
      : yFields.filter(field => field && field !== 'value');

    // Map fieldKey → display label and color (lookups when rendering)
    const seriesByKey = seriesMode
      ? new Map(config.series.map(s => [s.id, s]))
      : null;
    const labelFor = (key) => seriesByKey?.get(key)?.name || key;
    const colorOverride = (idx, key) => seriesByKey?.get(key)?.color || getColor(idx);
    
    // ✅ SMART DYNAMIC MARGINS - MINIMAL SPACING
    const smartMargins = calculateSmartMargins(data, filteredYFields, config.vizType);
    
    const hasLegend = config.showLegend && filteredYFields.length > 1;
    const hasRotatedXTicks = Math.abs(config.xAxisTickRotation ?? -45) > 0;
    const hasYAxisLabel = config.yAxisShowLabel !== false && config.yAxisLabel;
    const hasXAxisLabel = config.xAxisShowLabel !== false && config.xAxisLabel;
    
    // MINIMAL margin additions for labels
    const bottomMargin = smartMargins.bottom + (hasXAxisLabel ? 15 : 0);
    const leftMargin = smartMargins.left + (hasYAxisLabel ? 12 : 0);
    const topMargin = hasLegend ? 42 : 35; // Much tighter
    const rightMargin = smartMargins.right;

    // ================= ZOOM LOGIC =================
    const supportsZoom = ['line', 'area', 'bar'].includes(config.vizType);

    // zoomDomain uses data indices (not _time strings) so duplicate timestamps work
    const zoomedData = zoomDomain
      ? data.slice(zoomDomain.left, zoomDomain.right + 1)
      : data;

    const handleZoomMouseDown = (e) => {
      if (!supportsZoom) return;
      const idx = e?.activeTooltipIndex;
      if (idx === undefined || idx === null) return;
      isSelectingRef.current = true;
      selectionStartRef.current = idx;
      setSelectionStart(idx);
      setIsSelecting(true);
    };

    const handleZoomMouseMove = (e) => {
      if (!isSelectingRef.current) return;
      const idx = e?.activeTooltipIndex;
      if (idx === undefined || idx === null) return;
      setSelectionEnd(idx);
    };

    const handleZoomMouseUp = (e) => {
      if (!isSelectingRef.current) return;
      isSelectingRef.current = false;
      setIsSelecting(false);
      const startRaw = selectionStartRef.current;
      const endRaw = e?.activeTooltipIndex ?? selectionEnd;
      selectionStartRef.current = null;
      setSelectionStart(null);
      setSelectionEnd(null);
      if (startRaw === null || startRaw === undefined || endRaw === null || endRaw === undefined) return;
      if (startRaw === endRaw) return; // single click, no drag
      // Adjust for current zoom offset (zoomedData indices → full data indices)
      const offset = zoomDomain?.left ?? 0;
      const left  = Math.min(startRaw, endRaw) + offset;
      const right = Math.max(startRaw, endRaw) + offset;
      setZoomDomain({ left, right });
    };

    const handleDoubleClick = () => {
      if (supportsZoom) setZoomDomain(null);
    };

    // Pinch-to-zoom + scroll-wheel handlers (attached non-passively via zoomContainerRef)
    const getTouchDist = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const handleTouchStart = (e) => {
      if (!supportsZoom || e.touches.length !== 2) return;
      const startIdx = zoomDomain?.left ?? 0;
      const endIdx   = zoomDomain?.right ?? data.length - 1;
      touchRef.current = { dist: getTouchDist(e.touches), startIdx, endIdx };
    };

    const handleTouchMove = (e) => {
      if (!supportsZoom || e.touches.length !== 2 || !touchRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      const newDist = getTouchDist(e.touches);
      const scale = touchRef.current.dist / newDist;
      const { startIdx, endIdx } = touchRef.current;
      const center = Math.round((startIdx + endIdx) / 2);
      const half = Math.round(((endIdx - startIdx) * scale) / 2);
      const newStart = Math.max(0, center - half);
      const newEnd = Math.min(data.length - 1, center + half);
      if (newEnd - newStart >= 2) {
        setZoomDomain({ left: newStart, right: newEnd });
        touchRef.current.dist = newDist;
        touchRef.current.startIdx = newStart;
        touchRef.current.endIdx = newEnd;
      }
    };

    const handleWheel = (e) => {
      if (!supportsZoom) return;
      e.preventDefault();
      e.stopPropagation();

      const startIdx   = zoomDomain?.left ?? 0;
      const endIdx     = zoomDomain?.right ?? data.length - 1;
      const currentLen = endIdx - startIdx;

      // Map cursor X position to a ratio within the chart's plot area
      // (subtract left/right axis margins so zoom anchors to the data point under cursor)
      let mouseRatio = 0.5;
      const el = zoomContainerRef.current;
      if (el) {
        const rect      = el.getBoundingClientRect();
        const plotLeft  = rect.left  + leftMargin;
        const plotRight = rect.right - rightMargin;
        const plotWidth = plotRight  - plotLeft;
        if (plotWidth > 0) {
          mouseRatio = Math.max(0, Math.min(1, (e.clientX - plotLeft) / plotWidth));
        }
      }

      // Zoom keeping the data point under the cursor fixed in place
      const zoomFactor = e.deltaY > 0 ? 1.25 : 0.8;
      const newLen     = Math.round(currentLen * zoomFactor);
      const mouseIdx   = startIdx + mouseRatio * currentLen;
      const newStart   = Math.max(0, Math.round(mouseIdx - mouseRatio * newLen));
      const newEnd     = Math.min(data.length - 1, Math.round(mouseIdx + (1 - mouseRatio) * newLen));

      if (newEnd - newStart < 2) return;
      if (newStart === 0 && newEnd === data.length - 1) {
        setZoomDomain(null);
      } else {
        setZoomDomain({ left: newStart, right: newEnd });
      }
    };

    // Update handler refs so the non-passive useEffect listeners always call the latest version
    wheelHandlerRef.current = handleWheel;
    touchMoveHandlerRef.current = handleTouchMove;

    const zoomProps = supportsZoom ? {
      onMouseDown: handleZoomMouseDown,
      onMouseMove: handleZoomMouseMove,
      onMouseUp: handleZoomMouseUp,
      onDoubleClick: handleDoubleClick,
    } : {};

    // Map selection indices → _time strings for ReferenceArea visual overlay
    const selectionX1 = selectionStart !== null ? zoomedData[selectionStart]?._time : null;
    const selectionX2 = selectionEnd   !== null ? zoomedData[selectionEnd]?._time   : null;

    // Reset button: positioned below the action buttons (top:36) to avoid overlap
    const zoomOverlay = zoomDomain ? (
      <button
        onClick={() => setZoomDomain(null)}
        title="Reset zoom (or double-click chart)"
        style={{
          position: 'absolute', top: 36, right: 6,
          background: 'rgba(167,139,250,0.15)',
          border: '1px solid rgba(167,139,250,0.4)',
          borderRadius: '4px', color: '#a78bfa',
          fontSize: '10px', padding: '2px 6px',
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3,
          zIndex: 5
        }}
      >
        <ZoomIn size={10} /> Reset
      </button>
    ) : null;

    const zoomDivStyle = { position: 'relative', width: '100%', height: '100%' };
    // onTouchMove and onWheel are attached as non-passive listeners via zoomContainerRef
    // so they can call preventDefault() and stop page scroll/zoom
    const zoomDivHandlers = {
      ref: setZoomRef,  // callback ref: attaches wheel+touchmove listeners when div mounts
      onTouchStart: handleTouchStart,
      onTouchEnd: () => { touchRef.current = null; },
    };
    // =================================================

    const chartProps = {
      data: supportsZoom ? zoomedData : data,
      margin: {
        top: topMargin,
        right: rightMargin,
        left: leftMargin,
        bottom: bottomMargin
      }
    };
    
    const yDomain = getYAxisDomain();
    const yAxisScaleType = config.yAxisScale === 'log' ? 'log' : config.yAxisScale === 'linear' ? 'linear' : 'auto';
    console.log('📊 Using Y-axis scale:', yAxisScaleType, 'domain:', yDomain);

    const xAxisConfig = {
      dataKey: "_time",
      tick: config.xAxisShowTicks !== false ? { 
        fill: theme.chartText,
        fontSize: config.xAxisTickFontSize ?? (fontSize - 1),
        angle: config.xAxisTickRotation ?? -45, // Default to -45 for long timestamps
        textAnchor: (config.xAxisTickRotation ?? -45) !== 0 ? 'end' : 'middle',
        dy: (config.xAxisTickRotation ?? -45) !== 0 ? 5 : 0
      } : false,
      label: (config.xAxisShowLabel !== false && config.xAxisLabel) ? {
        value: config.xAxisLabel,
        position: 'insideBottom',
        offset: -8,
        fontSize: config.xAxisLabelFontSize ?? 11,
        angle: config.xAxisLabelRotation ?? 0,
        fill: theme.text
      } : undefined,
      tickCount: config.xAxisTickCount !== 'auto' ? parseInt(config.xAxisTickCount) : undefined,
      interval: config.xAxisTickInterval !== 'auto' ? parseInt(config.xAxisTickInterval) : 'equidistantPreserveStart',
      stroke: theme.chartAxis,
      tickLine: { stroke: theme.chartAxis },
      height: Math.abs(config.xAxisTickRotation ?? -45) > 0 ? (isSmall ? 70 : isMedium ? 80 : 90) : undefined,
      tickMargin: 10,
      minTickGap: 10
    };

    const yAxisConfig = {
      tick: config.yAxisShowTicks !== false ? { 
        fill: theme.chartText,
        fontSize: config.yAxisTickFontSize ?? (fontSize - 1),
        angle: config.yAxisTickRotation ?? 0,
        textAnchor: (config.yAxisTickRotation ?? 0) !== 0 ? 'end' : 'end',
        dx: -3
      } : false,
      tickFormatter: formatTickValue,
      label: (config.yAxisShowLabel !== false && config.yAxisLabel) ? {
        value: config.yAxisLabel,
        angle: -90,
        position: 'insideLeft',
        fontSize: config.yAxisLabelFontSize ?? 10,
        fill: theme.text,
        offset: 0
      } : undefined,
      domain: yDomain,
      scale: yAxisScaleType,
      width: config.yAxisWidth !== 'auto' ? parseInt(config.yAxisWidth) : smartMargins.left + (hasYAxisLabel ? 10 : 0),
      orientation: config.yAxisPosition || 'left',
      tickCount: config.yAxisTickCount !== 'auto' ? parseInt(config.yAxisTickCount) : undefined,
      stroke: theme.chartAxis,
      tickLine: { stroke: theme.chartAxis },
      tickMargin: 5
    };

    const gridConfig = config.showGrid ? {
      strokeDasharray: config.gridStrokeDashArray || '3 3',
      stroke: darkMode 
        ? `rgba(148, 163, 184, ${config.gridOpacity ?? 0.1})` 
        : `rgba(0, 0, 0, ${config.gridOpacity ?? 0.1})`
    } : false;

    // ✅ DYNAMIC legend configuration based on user preference
    const legendPosition = config.legendPosition || 'top'; // Default to top
    const isVerticalLegend = legendPosition === 'left' || legendPosition === 'right';
    const isHorizontalLegend = legendPosition === 'top' || legendPosition === 'bottom';
    
    const legendConfig = config.showLegend ? {
      wrapperStyle: { 
        color: theme.text, 
        fontSize: `${fontSize}px`,
        paddingTop: legendPosition === 'top' ? '5px' : '0',
        paddingBottom: legendPosition === 'bottom' ? '5px' : '0',
        paddingLeft: legendPosition === 'left' ? '5px' : '0',
        paddingRight: legendPosition === 'right' ? '5px' : '0'
      },
      layout: isVerticalLegend ? 'vertical' : 'horizontal',
      verticalAlign: isHorizontalLegend ? legendPosition : 'middle',
      align: isVerticalLegend ? legendPosition : 'center',
      iconSize: isSmall ? 8 : 9,
      iconType: 'line',
      wrapperMargin: { top: 0, bottom: 0 }
    } : null;

    // ── Shared rich tooltip content — used by Line, Area, Bar ────────────
    // Renders a Power BI-style hover card with an uppercase eyebrow, big
    // X label, per-series rows with colour dots, and — for time-series
    // charts — a tiny sparkline showing the hovered position in context.
    const richTooltip = ({ active, payload, label }) => {
      if (!active || !payload || !payload.length) return null;
      // Deduplicate — Recharts sometimes emits both the visible series
      // and hidden helper bars (e.g. waterfall floor). Drop null values.
      const rows = payload
        .filter(p => p && p.value !== undefined && p.value !== null && p.dataKey !== 'floor')
        .filter(p => p.name !== undefined);

      // Find the hovered X position in the panel's data so we can render
      // a compact sparkline of the surrounding N points.
      const hovered = rows[0]?.payload;
      let sparkPoints = [];
      const primaryKey = rows[0]?.dataKey;
      if (config.vizType === 'line' || config.vizType === 'area') {
        if (primaryKey && Array.isArray(data)) {
          sparkPoints = data.slice(-Math.min(24, data.length))
            .map((r, i) => ({ i, v: Number(r[primaryKey]) }))
            .filter(p => Number.isFinite(p.v));
        }
      }

      return (
        <div style={{
          background: darkMode ? 'rgba(15,23,42,0.96)' : 'rgba(255,255,255,0.98)',
          border: `1px solid ${theme.border}`,
          borderRadius: 8,
          padding: '10px 12px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
          color: theme.text, fontSize,
          minWidth: 160, maxWidth: 300
        }}>
          <div style={{
            fontSize: fontSize - 1, color: theme.textMuted,
            marginBottom: 4, fontWeight: 500,
            textTransform: 'uppercase', letterSpacing: 0.4
          }}>
            {rows.length > 1 ? `${rows.length} series` : (rows[0]?.name || 'value')}
          </div>
          <div style={{
            fontWeight: 700, marginBottom: 6,
            fontSize: fontSize + 1, wordBreak: 'break-word'
          }}>
            {label}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {rows.map((r, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between'
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  minWidth: 0, flex: 1
                }}>
                  <span style={{
                    width: 8, height: 8, borderRadius: 2,
                    background: r.color || r.fill || getColor(i),
                    flexShrink: 0
                  }} />
                  <span style={{
                    color: theme.textMuted,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                  }}>{r.name}</span>
                </div>
                <span style={{
                  fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                  color: r.color || r.fill || getColor(i)
                }}>
                  {formatTickValue(r.value)}
                </span>
              </div>
            ))}
          </div>
          {sparkPoints.length > 3 && (
            <div style={{
              marginTop: 8, paddingTop: 6, borderTop: `1px solid ${theme.border}`,
              height: 30
            }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={sparkPoints} margin={{ top: 1, right: 1, bottom: 1, left: 1 }}>
                  <defs>
                    <linearGradient id={`richSpark_${config.id}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={getColor(0)} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={getColor(0)} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area type="monotone" dataKey="v" stroke={getColor(0)}
                    strokeWidth={1.4} fill={`url(#richSpark_${config.id})`}
                    isAnimationActive={false} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      );
    };

    switch (config.vizType) {
      case 'line':
        return (
          <div style={zoomDivStyle} {...zoomDivHandlers}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart {...chartProps} {...zoomProps}>
                {gridConfig && <CartesianGrid {...gridConfig} />}
                <XAxis {...xAxisConfig} />
                <YAxis {...yAxisConfig} />
                <Tooltip
                  cursor={{ stroke: theme.textMuted, strokeDasharray: '3 3', strokeOpacity: 0.4 }}
                  content={richTooltip}
                />
                {legendConfig && <Legend {...legendConfig} />}
                {filteredYFields.map((yField, idx) => (
                  <Line
                    key={yField}
                    type="monotone"
                    dataKey={yField}
                    stroke={colorOverride(idx, yField)}
                    strokeWidth={isSmall ? 1.5 : config.lineWidth}
                    dot={config.showDots && !isSmall}
                    name={labelFor(yField)}
                    isAnimationActive={false}
                    // Cross-filter: fire when the user clicks a data point on
                    // the line (works in simple X-axis mode only).
                    activeDot={{
                      r: 5,
                      cursor: 'pointer',
                      onClick: (_, payload) => handleMarkClick(payload)
                    }}
                  />
                ))}
                {isSelecting && selectionX1 && selectionX2 && (
                  <ReferenceArea
                    x1={selectionX1} x2={selectionX2}
                    strokeOpacity={0.3}
                    fill="rgba(167,139,250,0.15)"
                    stroke="#a78bfa"
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
            {zoomOverlay}
          </div>
        );

      case 'area':
        return (
          <div style={zoomDivStyle} {...zoomDivHandlers}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart {...chartProps} {...zoomProps}>
                {gridConfig && <CartesianGrid {...gridConfig} />}
                <XAxis {...xAxisConfig} />
                <YAxis {...yAxisConfig} />
                <Tooltip
                  cursor={{ stroke: theme.textMuted, strokeDasharray: '3 3', strokeOpacity: 0.4 }}
                  content={richTooltip}
                />
                {legendConfig && <Legend {...legendConfig} />}
                {filteredYFields.map((yField, idx) => (
                  <Area
                    key={yField}
                    type="monotone"
                    dataKey={yField}
                    stroke={colorOverride(idx, yField)}
                    fill={colorOverride(idx, yField)}
                    fillOpacity={config.fillOpacity}
                    strokeWidth={isSmall ? 1.5 : config.lineWidth}
                    name={labelFor(yField)}
                    isAnimationActive={false}
                    // Cross-filter on active-dot click (simple X-axis mode only).
                    activeDot={{
                      r: 5,
                      cursor: 'pointer',
                      onClick: (_, payload) => handleMarkClick(payload)
                    }}
                  />
                ))}
                {isSelecting && selectionX1 && selectionX2 && (
                  <ReferenceArea
                    x1={selectionX1} x2={selectionX2}
                    strokeOpacity={0.3}
                    fill="rgba(167,139,250,0.15)"
                    stroke="#a78bfa"
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
            {zoomOverlay}
          </div>
        );

      case 'bar':
        return (
          <div style={zoomDivStyle} {...zoomDivHandlers}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart {...chartProps} {...zoomProps}>
                {gridConfig && <CartesianGrid {...gridConfig} />}
                <XAxis {...xAxisConfig} />
                <YAxis {...yAxisConfig} />
                <Tooltip
                  cursor={{ stroke: theme.textMuted, strokeDasharray: '3 3', strokeOpacity: 0.4 }}
                  content={richTooltip}
                />
                {legendConfig && <Legend {...legendConfig} />}
                {filteredYFields.map((yField, idx) => (
                  <Bar
                    key={yField}
                    dataKey={yField}
                    fill={colorOverride(idx, yField)}
                    name={labelFor(yField)}
                    isAnimationActive={false}
                    onClick={(entry) => handleMarkClick(entry)}
                    cursor="pointer"
                  />
                ))}
                {isSelecting && selectionX1 && selectionX2 && (
                  <ReferenceArea
                    x1={selectionX1} x2={selectionX2}
                    strokeOpacity={0.3}
                    fill="rgba(167,139,250,0.15)"
                    stroke="#a78bfa"
                  />
                )}
              </BarChart>
            </ResponsiveContainer>
            {zoomOverlay}
          </div>
        );

      case 'pie':
        const pieDataKey = filteredYFields[0] || config.yAxis;
        const pieHasLegend = config.showLegend && data.length > 1;
        const pieRadius = isSmall ? 45 : isMedium ? 60 : 75;
        const pieLegendPosition = config.legendPosition || 'top';
        const isPieVerticalLegend = pieLegendPosition === 'left' || pieLegendPosition === 'right';
        
        const pieLegendConfig = pieHasLegend ? {
          wrapperStyle: { 
            color: theme.text, 
            fontSize: `${fontSize}px`,
            paddingTop: pieLegendPosition === 'top' ? '5px' : '0',
            paddingBottom: pieLegendPosition === 'bottom' ? '5px' : '0'
          },
          layout: isPieVerticalLegend ? 'vertical' : 'horizontal',
          verticalAlign: (pieLegendPosition === 'top' || pieLegendPosition === 'bottom') ? pieLegendPosition : 'middle',
          align: isPieVerticalLegend ? pieLegendPosition : 'center',
          iconSize: 8
        } : null;
        
        const pieMargin = { 
          top: pieHasLegend ? 48 : 35, 
          right: 20, 
          bottom: 30, 
          left: 20 
        };
        
        const pieDataWithColors = data.slice(0, 10).map((entry, index) => ({
          ...entry,
          fill: PANEL_COLORS[index % PANEL_COLORS.length]
        }));

        return (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={pieMargin}>
              <Pie
                data={pieDataWithColors}
                dataKey={pieDataKey}
                nameKey="_time"
                onClick={(entry) => handleMarkClick(entry)}
                cursor="pointer"
                cx="50%"
                cy="50%"
                outerRadius={pieRadius}
                label={!isSmall && {
                  fill: theme.text,
                  fontSize: fontSize - 1
                }}
                isAnimationActive={true}
              >
                {pieDataWithColors.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload || !payload.length) return null;
                  const item = payload[0];
                  const color = item.payload?.fill || item.fill || item.color || PANEL_COLORS[0];
                  return (
                    <div style={{ background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '6px', padding: '6px 10px', color: theme.text, fontSize: `${fontSize}px` }}>
                      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '2px', background: color, marginRight: 6, verticalAlign: 'middle' }} />
                      {item.name} : {formatTickValue(item.value)}
                    </div>
                  );
                }}
              />
              {pieLegendConfig && (
                <Legend
                  {...pieLegendConfig}
                  content={() => (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center', padding: '2px 4px' }}>
                      {pieDataWithColors.map((entry, index) => (
                        <span key={index} style={{ display: 'flex', alignItems: 'center', gap: '4px', color: theme.text, fontSize: `${fontSize}px` }}>
                          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '2px', background: PANEL_COLORS[index % PANEL_COLORS.length], flexShrink: 0 }} />
                          {entry._time || entry[pieDataKey] || `Slice ${index + 1}`}
                        </span>
                      ))}
                    </div>
                  )}
                />
              )}
            </PieChart>
          </ResponsiveContainer>
        );

      case 'scatter':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart {...chartProps}>
              {gridConfig && <CartesianGrid {...gridConfig} />}
              <XAxis {...xAxisConfig} />
              <YAxis {...yAxisConfig} />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload || !payload.length) return null;
                  return (
                    <div style={{ background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '6px', padding: '6px 10px', color: theme.text, fontSize: `${fontSize}px` }}>
                      {payload[0]?.payload?._time && (
                        <div style={{ marginBottom: 4, color: theme.textSecondary, fontSize: `${fontSize - 1}px` }}>{payload[0].payload._time}</div>
                      )}
                      {payload.map((entry, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: entry.fill || entry.color || getColor(i), flexShrink: 0 }} />
                          {entry.name} : {formatTickValue(entry.value)}
                        </div>
                      ))}
                    </div>
                  );
                }}
              />
              {legendConfig && (
                <Legend
                  {...legendConfig}
                  content={() => (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center', padding: '2px 4px' }}>
                      {filteredYFields.map((field, idx) => (
                        <span key={idx} style={{ display: 'flex', alignItems: 'center', gap: '4px', color: theme.text, fontSize: `${fontSize}px` }}>
                          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: getColor(idx), flexShrink: 0 }} />
                          {field}
                        </span>
                      ))}
                    </div>
                  )}
                />
              )}
              {filteredYFields.map((yField, idx) => (
                <Scatter
                  key={yField}
                  dataKey={yField}
                  fill={colorOverride(idx, yField)}
                  name={labelFor(yField)}
                  isAnimationActive={true}
                  // Cross-filter on point click (simple X-axis mode only).
                  onClick={(entry) => handleMarkClick(entry)}
                  cursor="pointer"
                />
              ))}
            </ScatterChart>
          </ResponsiveContainer>
        );

      case 'radar':
        // Pivot data: each spoke = a metric name, not a timestamp
        // Take up to 8 recent time snapshots for comparison
        const radarSnapshots = data.slice(-Math.min(8, data.length));

        // Build pivoted data: [{ metric: "ga_ramp_1", "Latest": 261, "06:30": 250, ... }, ...]
        const radarData = filteredYFields.map(field => {
          const point = { metric: field };
          if (radarSnapshots.length === 1) {
            point.value = radarSnapshots[0][field] ?? 0;
          } else {
            radarSnapshots.forEach((row, i) => {
              const label = i === radarSnapshots.length - 1 ? 'Latest' : (row._time || `T${i + 1}`);
              point[label] = row[field] ?? 0;
            });
          }
          return point;
        });

        // Determine radar series keys (time snapshots)
        const radarSeriesKeys = radarSnapshots.length === 1
          ? ['value']
          : radarSnapshots.map((row, i) => i === radarSnapshots.length - 1 ? 'Latest' : (row._time || `T${i + 1}`));

        const radarHasLegend = config.showLegend && radarSeriesKeys.length > 1;
        const radarLegendPosition = config.legendPosition || 'top';
        const isRadarVerticalLegend = radarLegendPosition === 'left' || radarLegendPosition === 'right';

        const radarMargin = isSmall
          ? { top: radarHasLegend ? 48 : 32, right: 25, bottom: 32, left: 25 }
          : isMedium
          ? { top: radarHasLegend ? 52 : 35, right: 28, bottom: 35, left: 28 }
          : { top: radarHasLegend ? 55 : 38, right: 30, bottom: 38, left: 30 };

        const radarLegendConfig = radarHasLegend ? {
          wrapperStyle: {
            color: theme.text,
            fontSize: `${fontSize}px`,
            paddingTop: radarLegendPosition === 'top' ? '5px' : '0',
            paddingBottom: radarLegendPosition === 'bottom' ? '5px' : '0'
          },
          layout: isRadarVerticalLegend ? 'vertical' : 'horizontal',
          verticalAlign: (radarLegendPosition === 'top' || radarLegendPosition === 'bottom') ? radarLegendPosition : 'middle',
          align: isRadarVerticalLegend ? radarLegendPosition : 'center',
          iconSize: 8,
          iconType: 'line'
        } : null;

        return (
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData} margin={radarMargin}>
              <PolarGrid stroke={theme.chartGrid} />
              <PolarAngleAxis
                dataKey="metric"
                tick={{ fill: theme.chartText, fontSize: fontSize - 1 }}
                tickLine={false}
              />
              <PolarRadiusAxis
                tick={{ fill: theme.chartText, fontSize: fontSize - 1 }}
                angle={30}
                tickFormatter={(value) => formatTickValue(value)}
              />
              {radarSeriesKeys.map((key, idx) => (
                <Radar
                  key={key}
                  dataKey={key}
                  stroke={colorOverride(idx, yField)}
                  fill={colorOverride(idx, yField)}
                  fillOpacity={config.fillOpacity || 0.5}
                  name={key}
                  isAnimationActive={true}
                />
              ))}
              <Tooltip
                contentStyle={{
                  background: theme.card,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '6px',
                  color: theme.text,
                  fontSize: `${fontSize}px`
                }}
                formatter={(value) => formatTickValue(value)}
              />
              {radarLegendConfig && <Legend {...radarLegendConfig} />}
            </RadarChart>
          </ResponsiveContainer>
        );

      case 'stat': {
        // Power BI-style KPI: big current value, delta vs comparison,
        // inline sparkline of the whole window, min/max/avg row.
        // config.kpiCompareTo: 'first' (default) | 'previous' | 'average'
        //   controls what "delta" compares to.
        // config.kpiShowSparkline (default true), config.kpiShowStats (default true)
        const statField = filteredYFields[0] || config.yAxis;
        const numericValues = data
          .map(d => Number(d[statField]))
          .filter(v => Number.isFinite(v));

        // Empty state — no data at all
        if (numericValues.length === 0) {
          return (
            <div style={{
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              height: '100%', color: theme.textMuted,
              fontSize: isSmall ? '11px' : '13px', padding: 12
            }}>
              No data
            </div>
          );
        }

        const latest = numericValues[numericValues.length - 1];
        const first  = numericValues[0];
        const prev   = numericValues[numericValues.length - 2] ?? latest;
        const avg    = numericValues.reduce((a, b) => a + b, 0) / numericValues.length;
        const min    = Math.min(...numericValues);
        const max    = Math.max(...numericValues);

        // Delta baseline — default to first-in-window ("vs. period start")
        // which reads well on live dashboards. Configurable via kpiCompareTo.
        const compareMode = config.kpiCompareTo || 'first';
        const baseline =
          compareMode === 'previous' ? prev :
          compareMode === 'average'  ? avg  :
          first;
        const compareLabel =
          compareMode === 'previous' ? 'vs. prev' :
          compareMode === 'average'  ? 'vs. avg'  :
          'vs. start';

        const change   = latest - baseline;
        const changePct = baseline !== 0 ? (change / baseline) * 100 : 0;
        const goodDir = (config.kpiGoodDirection || 'up'); // 'up' | 'down' | 'none'
        // Colour signal: green when moving in the "good" direction, red the
        // other way. 'none' stays neutral (accent), useful for metrics where
        // higher isn't inherently better (temperature, pressure).
        const isGood =
          goodDir === 'none' ? null :
          goodDir === 'up'   ? change >= 0 :
          change <= 0;
        const deltaColour =
          isGood === null ? theme.textSecondary :
          isGood ? '#10b981' : '#ef4444';
        const arrow = change > 0 ? '▲' : change < 0 ? '▼' : '▶';

        // Visual alerts — evaluate rules top-to-bottom, first match wins.
        // The matched rule tints the big number + panel background.
        const evalOp = (op, v, target) => {
          const t = Number(target);
          if (!Number.isFinite(t)) return false;
          switch (op) {
            case '>=': return v >= t;
            case '<=': return v <= t;
            case '>':  return v > t;
            case '<':  return v < t;
            case '=':  return v === t;
            case '!=': return v !== t;
            default:   return false;
          }
        };
        const activeAlert = (config.visualAlerts || [])
          .find(r => r && r.op && evalOp(r.op, latest, r.value));
        const alertColor = activeAlert?.color;

        // Sparkline: last N points as a compact line. Hide on tiny panels
        // (isSmall && very cramped) — the big number is the priority.
        const showSparkline = config.kpiShowSparkline !== false;
        const sparkPoints = numericValues.slice(-Math.min(60, numericValues.length))
          .map((v, i) => ({ i, v }));

        const showStats = config.kpiShowStats !== false;
        // Comparison delta badge ("▲ x% vs. start"). Default on for backward
        // compatibility with saved panels that predate this flag.
        const showComparison = config.kpiShowComparison !== false;

        const statFontSize   = isSmall ? '30px' : isMedium ? '44px' : '56px';
        const changeFontSize = isSmall ? '11px' : isMedium ? '13px' : '14px';
        const metricFontSize = isSmall ? '10px' : isMedium ? '11px' : '12px';

        return (
          <div style={{
            display: 'flex', flexDirection: 'column', height: '100%',
            paddingTop: isSmall ? '32px' : '38px',
            paddingBottom: isSmall ? '32px' : '38px',
            paddingLeft: 12, paddingRight: 12,
            overflow: 'hidden',
            // Alert tint bathes the whole card in a soft wash of the alert
            // colour so the state reads even from across the room.
            background: alertColor ? `${alertColor}18` : 'transparent',
            transition: 'background 0.25s ease'
          }}>
            {/* Alert badge — sits inline just above the big value, so it
                never collides with the header action buttons on the right. */}
            {activeAlert && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 8px', borderRadius: 999,
                background: `${alertColor}30`,
                border: `1px solid ${alertColor}88`,
                color: alertColor,
                fontSize: 10, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 0.4,
                marginBottom: 4, alignSelf: 'flex-start',
                maxWidth: '100%', whiteSpace: 'nowrap',
                overflow: 'hidden', textOverflow: 'ellipsis'
              }}>
                <span style={{
                  width: 6, height: 6, borderRadius: 999, background: alertColor,
                  boxShadow: `0 0 6px ${alertColor}`,
                  animation: 'pulse 1.6s infinite',
                  flexShrink: 0
                }} />
                {activeAlert.label || 'Alert'}
              </div>
            )}

            {/* Big value + delta chip */}
            <div style={{
              display: 'flex', alignItems: 'baseline',
              gap: 10, flexWrap: 'wrap', marginBottom: 4
            }}>
              <div style={{
                fontSize: statFontSize, fontWeight: 700,
                color: alertColor || getColor(0),
                lineHeight: 1, letterSpacing: '-0.02em',
                transition: 'color 0.25s ease'
              }}>
                {formatTickValue(latest)}
              </div>
              {showComparison && (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 8px', borderRadius: 999,
                  background: isGood === null
                    ? 'rgba(148,163,184,0.14)'
                    : isGood ? 'rgba(16,185,129,0.14)' : 'rgba(239,68,68,0.14)',
                  border: `1px solid ${deltaColour}44`,
                  fontSize: changeFontSize, fontWeight: 600, color: deltaColour
                }}>
                  <span>{arrow}</span>
                  <span>{Math.abs(changePct).toFixed(1)}%</span>
                  <span style={{
                    color: theme.textMuted, fontWeight: 500, fontSize: metricFontSize
                  }}>
                    {compareLabel}
                  </span>
                </div>
              )}
            </div>

            {/* Field label + delta absolute value */}
            <div style={{
              fontSize: metricFontSize, color: theme.textMuted,
              marginBottom: showSparkline || showStats ? 8 : 0
            }}>
              {statField}{showComparison && change !== 0 && (
                <> · <span style={{ color: deltaColour, fontWeight: 500 }}>
                  {change > 0 ? '+' : ''}{formatNumber(change)}
                </span></>
              )}
            </div>

            {/* Sparkline */}
            {showSparkline && sparkPoints.length > 1 && (
              <div style={{ flex: 1, minHeight: isSmall ? 30 : 48, marginBottom: 6 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={sparkPoints} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                    <defs>
                      <linearGradient id={`kpiSpark_${config.id}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%"   stopColor={getColor(0)} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={getColor(0)} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <Area
                      type="monotone"
                      dataKey="v"
                      stroke={getColor(0)}
                      strokeWidth={1.6}
                      fill={`url(#kpiSpark_${config.id})`}
                      isAnimationActive={false}
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Stats row */}
            {showStats && (
              <div style={{
                display: 'flex', gap: isSmall ? 10 : 18,
                fontSize: metricFontSize, color: theme.textMuted,
                flexWrap: 'wrap'
              }}>
                <div>min <span style={{ color: theme.text, fontWeight: 600 }}>{formatTickValue(min)}</span></div>
                <div>max <span style={{ color: theme.text, fontWeight: 600 }}>{formatTickValue(max)}</span></div>
                <div>avg <span style={{ color: theme.text, fontWeight: 600 }}>{formatTickValue(avg)}</span></div>
              </div>
            )}
          </div>
        );
      }

      case 'calendarHeatmap': {
        // GitHub-style contribution grid: 7 rows (days of week) × N columns
        // (weeks), each cell colour-encoding the daily aggregate of a metric.
        // Uses the timestamp column on config.timestampField and aggregates
        // the first Y field. Aggregation mode: 'sum' | 'avg' | 'max' (default sum).
        const heatField = filteredYFields[0] || config.yAxis;
        const tsKey = config.timestampField || '_timestamp';
        const aggMode = config.heatmapAggregation || 'sum';

        // Group values by YYYY-MM-DD
        const byDay = new Map();
        for (const row of data) {
          const raw = row[tsKey] ?? row._timestamp ?? row._time;
          if (!raw) continue;
          const d = new Date(raw);
          if (isNaN(d.getTime())) continue;
          const key = d.toISOString().slice(0, 10);
          const v = Number(row[heatField]);
          if (!Number.isFinite(v)) continue;
          const bucket = byDay.get(key) || { sum: 0, count: 0, max: -Infinity };
          bucket.sum += v; bucket.count += 1; bucket.max = Math.max(bucket.max, v);
          byDay.set(key, bucket);
        }

        if (byDay.size === 0) {
          return (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: '100%', color: theme.textMuted,
              fontSize: isSmall ? '11px' : '13px'
            }}>
              No data with valid timestamps
            </div>
          );
        }

        const aggregate = (b) =>
          aggMode === 'avg' ? b.sum / b.count :
          aggMode === 'max' ? b.max :
          b.sum;

        // Build a contiguous day range from earliest → latest.
        const days = [...byDay.keys()].sort();
        const start = new Date(days[0]);
        const end   = new Date(days[days.length - 1]);
        // Pad start back to the previous Sunday so weeks align.
        const startAligned = new Date(start);
        startAligned.setDate(startAligned.getDate() - startAligned.getDay());
        const totalDays = Math.ceil((end - startAligned) / 86400000) + 1;
        const weeks = Math.ceil(totalDays / 7);

        // Find max value for colour normalisation
        let maxVal = 0;
        for (const b of byDay.values()) {
          const v = aggregate(b);
          if (v > maxVal) maxVal = v;
        }

        // Colour scale — 5 steps from muted → accent
        const baseAccent = getColor(0);
        const emptyBg = darkMode ? 'rgba(148,163,184,0.08)' : 'rgba(148,163,184,0.16)';
        const colourFor = (v) => {
          if (v <= 0 || maxVal === 0) return emptyBg;
          const t = Math.min(1, v / maxVal);
          // Linear interpolation on opacity — cheap and readable
          const alpha = 0.20 + 0.80 * t;
          return `${baseAccent}${Math.round(alpha * 255).toString(16).padStart(2, '0')}`;
        };

        // Cell sizing based on panel dimensions. Aim to fit all weeks in view.
        const cellGap = 2;
        // Panel width isn't directly known here — use CSS grid + let it scale.
        // Use fixed 12px cells with responsive font.
        const cellSize = isSmall ? 8 : isMedium ? 11 : 14;

        // Build cells grouped by week (columns)
        const cells = [];
        for (let w = 0; w < weeks; w++) {
          const col = [];
          for (let d = 0; d < 7; d++) {
            const dayOffset = w * 7 + d;
            const day = new Date(startAligned);
            day.setDate(day.getDate() + dayOffset);
            if (day < start || day > end) { col.push(null); continue; }
            const key = day.toISOString().slice(0, 10);
            const b = byDay.get(key);
            col.push({
              date: key,
              value: b ? aggregate(b) : 0,
              hasData: !!b
            });
          }
          cells.push(col);
        }

        const dayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

        // Sparse-data helper — a proper message beats a lonely dot in the grid
        if (days.length < 3) {
          return (
            <div style={{
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              height: '100%', color: theme.textMuted,
              fontSize: isSmall ? 11 : 12, gap: 6, padding: 12,
              paddingBottom: isSmall ? 32 : 38
            }}>
              <div style={{
                width: cellSize * 2, height: cellSize * 2,
                borderRadius: 3, background: colourFor(maxVal)
              }} />
              <div style={{ marginTop: 6 }}>
                Only {days.length} day{days.length !== 1 ? 's' : ''} of data · widen the time range
              </div>
              <div style={{ fontSize: isSmall ? 10 : 11 }}>
                max {formatTickValue(maxVal)}
              </div>
            </div>
          );
        }

        return (
          <div style={{
            display: 'flex', flexDirection: 'column', height: '100%',
            paddingTop: isSmall ? 30 : 36,
            // Room for the absolute footer bar at the bottom of every panel.
            paddingBottom: isSmall ? 32 : 38,
            paddingLeft: 12, paddingRight: 12, overflow: 'hidden'
          }}>
            {/* Grid area */}
            <div style={{
              flex: 1, minHeight: 0, overflow: 'auto',
              display: 'flex', alignItems: 'flex-start', gap: 6
            }}>
              {/* Day labels column */}
              <div style={{
                display: 'flex', flexDirection: 'column',
                gap: cellGap, paddingTop: 2, flexShrink: 0
              }}>
                {dayLabels.map((lab, i) => (
                  <div key={i} style={{
                    height: cellSize, lineHeight: `${cellSize}px`,
                    fontSize: Math.max(8, cellSize - 4),
                    color: theme.textMuted,
                    visibility: i % 2 === 1 ? 'visible' : 'hidden'
                  }}>{lab}</div>
                ))}
              </div>

              {/* Week columns */}
              <div style={{
                display: 'flex', gap: cellGap, flex: 1, minWidth: 0,
                overflowX: 'auto'
              }}>
                {cells.map((col, wi) => (
                  <div key={wi} style={{
                    display: 'flex', flexDirection: 'column', gap: cellGap,
                    flexShrink: 0
                  }}>
                    {col.map((cell, di) => (
                      <div key={di}
                        title={cell
                          ? `${cell.date} · ${formatTickValue(cell.value)}${cell.hasData ? '' : ' (no data)'}`
                          : ''}
                        style={{
                          width: cellSize, height: cellSize, borderRadius: 2,
                          background: cell ? colourFor(cell.value) : 'transparent',
                          cursor: cell?.hasData ? 'pointer' : 'default'
                        }}
                        onClick={() => {
                          if (cell?.hasData && config.xAxisMode === 'simple' && Array.isArray(config.xAxisFields) && config.xAxisFields[0]) {
                            handleMarkClick({ payload: { [config.xAxisFields[0]]: cell.date } });
                          }
                        }}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Legend line — kept compact and on its own row so it never runs
                into the info text or the panel's built-in footer strip. */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
              gap: 4, marginTop: 6, fontSize: isSmall ? 9 : 10,
              color: theme.textMuted, flexShrink: 0
            }}>
              <span style={{ marginRight: 4 }}>
                {days.length} days · max {formatTickValue(maxVal)}
              </span>
              <span>less</span>
              {[0, 0.25, 0.5, 0.75, 1].map(t => (
                <div key={t} style={{
                  width: cellSize - 2, height: cellSize - 2, borderRadius: 2,
                  background: t === 0 ? emptyBg
                    : `${baseAccent}${Math.round((0.20 + 0.80 * t) * 255).toString(16).padStart(2, '0')}`
                }} />
              ))}
              <span>more</span>
            </div>
          </div>
        );
      }

      case 'table': {
        const columns = Object.keys(data[0] || {}).filter(key => !key.startsWith('_'));
        const tableFontSize = isSmall ? '10px' : '12px';
        const headerFontSize = isSmall ? '9px' : '11px';

        // ── Conditional formatting: per-column stats + rules ──────────────
        // Precompute min/max per column for color scale + data bars normalisation.
        const columnFormats = Array.isArray(config.columnFormats) ? config.columnFormats : [];
        const columnStats = {};
        for (const rule of columnFormats) {
          if (columnStats[rule.column]) continue;
          const vals = data.map(r => Number(r[rule.column])).filter(v => Number.isFinite(v));
          if (!vals.length) continue;
          columnStats[rule.column] = { min: Math.min(...vals), max: Math.max(...vals) };
        }
        // Hex string interpolator for color scale
        const hexMix = (hexA, hexB, t) => {
          const a = hexA.replace('#', ''), b = hexB.replace('#', '');
          const parse = (h) => [
            parseInt(h.substring(0, 2), 16),
            parseInt(h.substring(2, 4), 16),
            parseInt(h.substring(4, 6), 16)
          ];
          const [ar, ag, ab] = parse(a), [br, bg, bb] = parse(b);
          const mix = (x, y) => Math.round(x + (y - x) * t);
          const to2 = (n) => n.toString(16).padStart(2, '0');
          return `#${to2(mix(ar, br))}${to2(mix(ag, bg))}${to2(mix(ab, bb))}`;
        };
        // Apply matching rule to a cell → returns { background, color, dataBar }
        const applyFormat = (col, val) => {
          if (typeof val !== 'number' || !Number.isFinite(val)) return null;
          const rule = columnFormats.find(r => r.column === col);
          if (!rule) return null;
          const stats = columnStats[col];
          if (rule.kind === 'colorScale' && stats && stats.max !== stats.min) {
            const t = (val - stats.min) / (stats.max - stats.min);
            const low  = rule.colorLow  || '#0f172a';
            const high = rule.colorHigh || '#7c3aed';
            return { background: hexMix(low, high, t) };
          }
          if (rule.kind === 'dataBars' && stats && stats.max !== 0) {
            const t = Math.max(0, Math.min(1, val / stats.max));
            return { dataBar: { fraction: t, color: rule.colorHigh || '#7c3aed' } };
          }
          if (rule.kind === 'textColor') {
            const above = Number(rule.threshold);
            if (Number.isFinite(above)) {
              return { color: val >= above ? (rule.above || '#10b981') : (rule.below || '#ef4444') };
            }
          }
          return null;
        };

        const formatCellValue = (col, val) => {
          if (val === null || val === undefined) return '—';
          if (typeof val === 'number') return formatTickValue(val);
          // shorten ISO timestamps: 2026-04-03T23:31:53.210Z → Apr 03, 23:31:53
          if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val)) {
            try {
              const d = new Date(val);
              const mon = d.toLocaleString('en', { month: 'short' });
              const day = String(d.getDate()).padStart(2, '0');
              const time = d.toTimeString().slice(0, 8);
              return `${mon} ${day}, ${time}`;
            } catch { return val; }
          }
          return String(val);
        };

        return (
          <div style={{
            position: 'absolute', inset: 0,
            paddingTop: isSmall ? '34px' : '38px',
            paddingBottom: isSmall ? '22px' : '26px',
            overflow: 'hidden',
            display: 'flex', flexDirection: 'column'
          }}>
            <div style={{ flex: 1, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: tableFontSize, tableLayout: 'auto' }}>
                <thead>
                  <tr style={{
                    position: 'sticky', top: 0, zIndex: 2
                  }}>
                    {columns.map(col => (
                      <th key={col} style={{
                        padding: isSmall ? '5px 8px' : '7px 12px',
                        textAlign: 'left',
                        borderBottom: `1px solid ${theme.border}`,
                        borderRight: `1px solid ${theme.border}`,
                        color: theme.textMuted,
                        fontWeight: '700',
                        fontSize: headerFontSize,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        whiteSpace: 'nowrap',
                        background: theme.card,
                      }}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={i} style={{
                      background: i % 2 === 0 ? 'transparent' : (darkMode ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.02)'),
                      borderBottom: `1px solid ${theme.border}`,
                      transition: 'background 0.1s'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = darkMode ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.06)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : (darkMode ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.02)'); }}
                    >
                      {columns.map((col, ci) => {
                        const val = row[col];
                        const isNum = typeof val === 'number';
                        const isTs = typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val);
                        const fmt = applyFormat(col, val);
                        return (
                          <td key={col} style={{
                            position: 'relative',
                            padding: isSmall ? '4px 8px' : '6px 12px',
                            color: fmt?.color || (isNum ? theme.accent : isTs ? theme.textMuted : theme.text),
                            background: fmt?.background,
                            borderRight: `1px solid ${theme.border}`,
                            fontWeight: isNum ? '600' : '400',
                            whiteSpace: 'nowrap',
                            fontVariantNumeric: isNum ? 'tabular-nums' : 'normal'
                          }}>
                            {/* Data bar renders behind the value */}
                            {fmt?.dataBar && (
                              <div style={{
                                position: 'absolute', left: 4, right: 4, top: 2, bottom: 2,
                                borderRadius: 3, pointerEvents: 'none', opacity: 0.28,
                                background: `linear-gradient(to right, ${fmt.dataBar.color} ${fmt.dataBar.fraction * 100}%, transparent ${fmt.dataBar.fraction * 100}%)`
                              }} />
                            )}
                            <span style={{ position: 'relative' }}>{formatCellValue(col, val)}</span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      case 'waterfall': {
        // Waterfall: running total broken down by per-row contribution.
        // xAxisFields[0] = category label, filteredYFields[0] = delta value.
        // Each bar consists of an invisible "floor" bar to lift it into
        // position and a coloured "delta" bar with the actual size.
        const labelCol = (Array.isArray(config.xAxisFields) && config.xAxisFields[0])
          || (data[0] && Object.keys(data[0]).find(k => !k.startsWith('_') && typeof data[0][k] !== 'number'));
        const valCol = filteredYFields[0] || config.yAxis;

        if (!labelCol || !valCol) {
          return (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: '100%', color: theme.textMuted, padding: 24,
              fontSize: isSmall ? 11 : 12, textAlign: 'center', lineHeight: 1.5
            }}>
              Waterfall needs a category (X) column and a delta (Y) column.
            </div>
          );
        }

        let running = 0;
        const bars = (data || [])
          .map(r => ({ name: String(r[labelCol] ?? ''), delta: Number(r[valCol]) }))
          .filter(r => Number.isFinite(r.delta))
          .map(r => {
            const start = running;
            running += r.delta;
            return {
              name: r.name,
              delta: r.delta,
              start,
              end: running,
              // Recharts stacks by additive numeric arrays — we split the
              // bar into a transparent floor + a coloured delta.
              floor: Math.min(start, running),
              size:  Math.abs(r.delta),
              pos:   r.delta >= 0
            };
          });

        if (bars.length === 0) {
          return (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: '100%', color: theme.textMuted,
              fontSize: isSmall ? 11 : 12
            }}>
              No numeric deltas to plot
            </div>
          );
        }

        // Add a final "Total" bar showing the cumulative running total.
        const withTotal = [...bars, {
          name: 'Total', delta: running, start: 0, end: running,
          floor: 0, size: Math.abs(running), pos: running >= 0, isTotal: true
        }];

        const posColor  = '#10b981';
        const negColor  = '#ef4444';
        const totalColor = getColor(0);

        return (
          <div style={{
            position: 'absolute', inset: 0,
            paddingTop: isSmall ? 32 : 38,
            paddingBottom: isSmall ? 30 : 34
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={withTotal} margin={{ top: 5, right: 20, left: 10, bottom: 10 }}>
                {gridConfig && <CartesianGrid {...gridConfig} />}
                <XAxis dataKey="name"
                  tick={{ fill: theme.chartText, fontSize: fontSize - 1 }}
                  tickLine={{ stroke: theme.chartAxis }}
                  axisLine={{ stroke: theme.chartAxis }} />
                <YAxis
                  tick={{ fill: theme.chartText, fontSize: fontSize - 1 }}
                  tickLine={{ stroke: theme.chartAxis }}
                  axisLine={{ stroke: theme.chartAxis }}
                  tickFormatter={formatTickValue} />
                <Tooltip
                  cursor={{ fill: darkMode ? 'rgba(139,92,246,0.06)' : 'rgba(124,58,237,0.05)' }}
                  content={({ active, payload, label }) => {
                    if (!active || !payload || !payload.length) return null;
                    // Recharts includes both the invisible "floor" and the
                    // coloured "size" segments in payload — grab the row
                    // itself off the size segment.
                    const sizeSeg = payload.find(p => p.dataKey === 'size') || payload[0];
                    const row = sizeSeg?.payload || {};
                    const isTotal = !!row.isTotal;
                    const dot = isTotal ? totalColor : (row.pos ? posColor : negColor);
                    const value = isTotal
                      ? formatTickValue(row.end)
                      : `${row.pos ? '+' : ''}${formatTickValue(row.delta)}`;
                    return (
                      <div style={{
                        background: darkMode ? 'rgba(15,23,42,0.96)' : 'rgba(255,255,255,0.98)',
                        border: `1px solid ${theme.border}`,
                        borderRadius: 8, padding: '8px 12px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                        color: theme.text, fontSize
                      }}>
                        <div style={{
                          fontSize: fontSize - 1, color: theme.textMuted,
                          fontWeight: 500, textTransform: 'uppercase',
                          letterSpacing: 0.4, marginBottom: 3
                        }}>
                          {isTotal ? 'Running total' : 'Contribution'}
                        </div>
                        <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 6,
                          fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                          color: dot
                        }}>
                          <span style={{
                            width: 8, height: 8, borderRadius: 2,
                            background: dot, display: 'inline-block'
                          }} />
                          {value}
                        </div>
                        {!isTotal && (
                          <div style={{
                            marginTop: 4, fontSize: fontSize - 1,
                            color: theme.textMuted
                          }}>
                            Running: <span style={{ color: theme.text, fontWeight: 600 }}>
                              {formatTickValue(row.end)}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  }}
                />
                {/* Invisible floor lifts each delta bar into position */}
                <Bar dataKey="floor" stackId="w" fill="transparent" isAnimationActive={false} />
                {/* Coloured delta */}
                <Bar dataKey="size" stackId="w" isAnimationActive={false}
                  radius={[3, 3, 0, 0]}
                  onClick={(entry) => handleMarkClick({ payload: { [labelCol]: entry?.payload?.name } })}
                  cursor="pointer"
                >
                  {withTotal.map((row, i) => (
                    <Cell key={i} fill={row.isTotal ? totalColor : (row.pos ? posColor : negColor)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        );
      }

      case 'sankey': {
        // Sankey needs three columns: source, target, value.
        // xAxisFields[0] = source column, xAxisFields[1] = target column,
        // filteredYFields[0] = value column.
        const srcCol = Array.isArray(config.xAxisFields) && config.xAxisFields[0];
        const tgtCol = Array.isArray(config.xAxisFields) && config.xAxisFields[1];
        const valCol = filteredYFields[0] || config.yAxis;

        if (!srcCol || !tgtCol || !valCol) {
          return (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: '100%', color: theme.textMuted, padding: 24,
              fontSize: isSmall ? 11 : 12, textAlign: 'center', lineHeight: 1.5
            }}>
              Sankey needs a source column, a target column, and a value column.<br />
              Set <strong>X-axis fields</strong> to [source, target] and <strong>Y-axis</strong> to the numeric value.
            </div>
          );
        }

        // Build node list + links. Node names are unique; each link maps
        // source-node-index → target-node-index with a value.
        const nodeMap = new Map();
        const addNode = (n) => {
          if (!nodeMap.has(n)) nodeMap.set(n, { name: n });
          return [...nodeMap.keys()].indexOf(n);
        };
        const links = [];
        for (const row of (data || [])) {
          const s = row[srcCol]; const t = row[tgtCol]; const v = Number(row[valCol]);
          if (s == null || t == null || !Number.isFinite(v) || v <= 0) continue;
          const si = addNode(String(s));
          const ti = addNode(String(t));
          if (si === ti) continue; // no self-loops (Sankey chokes)
          links.push({ source: si, target: ti, value: v });
        }
        const nodes = [...nodeMap.values()];

        // A useful sankey needs several distinct flows. A single link makes
        // it degenerate into one huge rectangle — show a hint instead.
        if (nodes.length < 3 || links.length < 2) {
          return (
            <div style={{
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              height: '100%', color: theme.textMuted, padding: 24,
              fontSize: isSmall ? 11 : 12, textAlign: 'center', lineHeight: 1.5, gap: 6
            }}>
              <div>Only {links.length} flow{links.length !== 1 ? 's' : ''} between {nodes.length} node{nodes.length !== 1 ? 's' : ''}.</div>
              <div style={{ fontSize: isSmall ? 10 : 11 }}>
                Sankey needs at least 3 distinct nodes and 2 flows to look meaningful.<br/>
                Check your source / target columns or widen the time range.
              </div>
            </div>
          );
        }

        // Custom node renderer — brand-coloured tile with a text label.
        const nodeRenderer = ({ x, y, width, height, index, payload }) => {
          const color = getColor(index || 0);
          return (
            <g>
              <rect x={x} y={y} width={width} height={height}
                fill={color} fillOpacity={0.92} rx={2} ry={2} />
              <text x={x + width + 6} y={y + height / 2 + 4}
                fill={theme.text}
                fontSize={isSmall ? 10 : 11} fontWeight={600}
                paintOrder="stroke"
                stroke={theme.card}
                strokeWidth={2}
                strokeLinejoin="round"
              >
                {payload?.name}
              </text>
            </g>
          );
        };

        // Custom link renderer — colours the flow using the source node's colour
        // and adds a soft gradient so many flows still read distinctly.
        const linkRenderer = ({ sourceX, targetX, sourceY, targetY,
                                sourceControlX, targetControlX,
                                linkWidth, payload }) => {
          const srcIdx = payload?.source?.index ?? 0;
          const color = getColor(srcIdx);
          const d = `M${sourceX},${sourceY}
                     C${sourceControlX},${sourceY}
                      ${targetControlX},${targetY}
                      ${targetX},${targetY}`;
          return (
            <path d={d} stroke={color} strokeOpacity={0.32}
              strokeWidth={linkWidth} fill="none" />
          );
        };

        return (
          <div style={{
            position: 'absolute', inset: 0,
            paddingTop: isSmall ? 32 : 38,
            paddingBottom: isSmall ? 30 : 34,
            paddingLeft: 8, paddingRight: 8
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <Sankey
                data={{ nodes, links }}
                node={nodeRenderer}
                link={linkRenderer}
                nodePadding={12}
                nodeWidth={12}
                margin={{ top: 5, right: 90, bottom: 5, left: 5 }}
              >
                <Tooltip
                  cursor={false}
                  content={({ active, payload }) => {
                    if (!active || !payload || !payload.length) return null;
                    const p = payload[0].payload || {};
                    const isLink = p.source && p.target;
                    const label = isLink
                      ? `${p.source.name} → ${p.target.name}`
                      : (p.name || '');
                    const value = isLink ? p.value : (p.value || 0);
                    return (
                      <div style={{
                        background: darkMode ? 'rgba(15,23,42,0.96)' : 'rgba(255,255,255,0.98)',
                        border: `1px solid ${theme.border}`,
                        borderRadius: 8,
                        padding: '8px 12px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                        fontSize: fontSize,
                        color: theme.text,
                        minWidth: 120
                      }}>
                        <div style={{
                          fontSize: fontSize - 1, color: theme.textMuted,
                          marginBottom: 3, fontWeight: 500,
                          textTransform: 'uppercase', letterSpacing: 0.4
                        }}>
                          {isLink ? 'Flow' : 'Node'}
                        </div>
                        <div style={{ fontWeight: 700, marginBottom: 4, wordBreak: 'break-word' }}>
                          {label}
                        </div>
                        <div style={{ color: getColor(0), fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                          {formatTickValue(value)}
                        </div>
                      </div>
                    );
                  }}
                />
              </Sankey>
            </ResponsiveContainer>
          </div>
        );
      }

      case 'treemap': {
        // Treemap: one rectangle per row, sized by the first Y field.
        // Uses xAxisFields[0] as the label — or falls back to _time / row 0 key.
        const valueField = filteredYFields[0] || config.yAxis;
        const labelField = (Array.isArray(config.xAxisFields) && config.xAxisFields[0])
          || (data[0] && Object.keys(data[0]).find(k => !k.startsWith('_') && typeof data[0][k] !== 'number'));
        const rows = (data || [])
          .map(r => ({
            name: labelField ? String(r[labelField] ?? '') : String(r._time ?? ''),
            size: Number(r[valueField])
          }))
          .filter(r => Number.isFinite(r.size) && r.size > 0)
          .slice(0, 40); // cap for readability

        if (rows.length === 0) {
          return (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: '100%', color: theme.textMuted,
              fontSize: isSmall ? 11 : 13
            }}>
              No positive-value rows to render
            </div>
          );
        }

        return (
          <div style={{
            position: 'absolute', inset: 0,
            paddingTop: isSmall ? 32 : 38,
            paddingBottom: isSmall ? 30 : 34,
            paddingLeft: 8, paddingRight: 8
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <Treemap
                data={rows}
                dataKey="size"
                stroke={theme.card}
                isAnimationActive={false}
                content={(props) => {
                  const { x, y, width, height, index, depth, name, size } = props;
                  // Skip root / non-leaf nodes.
                  if (depth === 0 || name == null || width <= 0 || height <= 0) return <g />;

                  const label = String(name);
                  const color = getColor(index || 0);
                  const pad = 2;
                  const rx = 4;
                  const showLabel = width > 44 && height > 26;
                  const showValue = width > 44 && height > 44;

                  // Label truncation based on rectangle width so text never
                  // overflows the tile. ~6.5px per char at 11px font.
                  const charsFit = Math.floor((width - 12) / 6.5);
                  const clipped = label.length > charsFit
                    ? label.slice(0, Math.max(1, charsFit - 1)) + '…'
                    : label;

                  return (
                    <g style={{ cursor: 'pointer' }}
                       onClick={() => handleMarkClick({ payload: { [labelField]: label } })}>
                      <rect
                        x={x + pad} y={y + pad}
                        width={Math.max(0, width - pad * 2)}
                        height={Math.max(0, height - pad * 2)}
                        rx={rx} ry={rx}
                        fill={color} fillOpacity={0.92}
                      />
                      {showLabel && (
                        <text
                          x={x + 10} y={y + 18}
                          fill="#ffffff"
                          fontSize={isSmall ? 10 : 12}
                          fontWeight={700}
                          // SVG paint-order + stroke gives a crisp text
                          // outline that's readable on any tile colour,
                          // unlike the fuzzy CSS text-shadow that Recharts
                          // often drops.
                          paintOrder="stroke"
                          stroke="rgba(0,0,0,0.55)"
                          strokeWidth={2.5}
                          strokeLinejoin="round"
                        >
                          {clipped}
                        </text>
                      )}
                      {showValue && (
                        <text
                          x={x + 10} y={y + 34}
                          fill="rgba(255,255,255,0.95)"
                          fontSize={isSmall ? 9 : 11}
                          fontWeight={500}
                          paintOrder="stroke"
                          stroke="rgba(0,0,0,0.45)"
                          strokeWidth={2}
                          strokeLinejoin="round"
                        >
                          {formatTickValue(Number(size))}
                        </text>
                      )}
                    </g>
                  );
                }}
              />
            </ResponsiveContainer>
          </div>
        );
      }

      case 'funnel': {
        // Funnel: rows sorted descending by value form the funnel stages.
        // Uses xAxisFields[0] (or first non-numeric column) as stage label.
        const valueField = filteredYFields[0] || config.yAxis;
        const labelField = (Array.isArray(config.xAxisFields) && config.xAxisFields[0])
          || (data[0] && Object.keys(data[0]).find(k => !k.startsWith('_') && typeof data[0][k] !== 'number'));
        const rows = (data || [])
          .map(r => ({
            name: labelField ? String(r[labelField] ?? '') : String(r._time ?? ''),
            value: Number(r[valueField])
          }))
          .filter(r => Number.isFinite(r.value) && r.value > 0)
          .sort((a, b) => b.value - a.value);

        if (rows.length === 0) {
          return (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: '100%', color: theme.textMuted,
              fontSize: isSmall ? 11 : 13
            }}>
              No positive-value rows to render
            </div>
          );
        }

        // Attach a color per stage for the LabelList to pick up
        const funnelData = rows.map((r, i) => ({ ...r, fill: getColor(i) }));

        return (
          <div style={{
            position: 'absolute', inset: 0,
            paddingTop: isSmall ? 32 : 38,
            paddingBottom: isSmall ? 30 : 34
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <FunnelChart>
                <Tooltip
                  cursor={{ stroke: theme.textMuted, strokeDasharray: '3 3', strokeOpacity: 0.4 }}
                  content={richTooltip}
                />
                <Funnel
                  dataKey="value"
                  data={funnelData}
                  isAnimationActive={false}
                  onClick={(entry) => handleMarkClick({ payload: { [labelField]: entry?.payload?.name ?? entry?.name } })}
                  cursor="pointer"
                >
                  <LabelList
                    position="right"
                    fill={theme.text}
                    stroke="none"
                    dataKey="name"
                    style={{ fontSize, fontWeight: 600 }}
                  />
                </Funnel>
              </FunnelChart>
            </ResponsiveContainer>
          </div>
        );
      }

      default:
        return <div style={{ textAlign: 'center', color: theme.textMuted }}>Unsupported chart type</div>;
    }
  };

  const hasCrossFilters = Array.isArray(crossFilters) && crossFilters.length > 0;
  const accentBorder = hasCrossFilters ? '#8b5cf6' : theme.border;

  const panelInner = (
    <div
      style={{
        ...(focused ? {} : style),
        height: '100%',
        background: theme.card,
        border: `${hasCrossFilters ? '2px' : '1px'} solid ${accentBorder}`,
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: darkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 1px 4px rgba(0,0,0,0.06)',
        transition: 'all 0.2s ease',
        position: 'relative'
      }}
      onMouseEnter={(e) => e.currentTarget.style.boxShadow = darkMode ? '0 4px 12px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.1)'}
      onMouseLeave={(e) => e.currentTarget.style.boxShadow = darkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 1px 4px rgba(0,0,0,0.06)'}
      onContextMenu={(e) => {
        // Only intercept right-clicks on the chart body — leave the header
        // action buttons alone so their native browser context menu still
        // works (rare but useful for debugging).
        e.preventDefault();
        setCtxMenu({ x: e.clientX, y: e.clientY });
      }}
    >
      {/* Plant agent offline: a calm banner over the last data we rendered,
          never a red error wall and never a blanked panel. */}
      {offline && (
        <div style={{
          position: 'absolute',
          top: isSmall ? 26 : 34,
          left: 6,
          right: 6,
          zIndex: 12,
          pointerEvents: 'none'
        }}>
          <PlantOfflineBanner
            plantId={offline.plantId || config.plantId}
            since={offline.since}
            lastDataTs={offline.lastDataTs}
            compact={isSmall}
          />
        </div>
      )}

      {ctxMenu && (
        <PanelContextMenu
          x={ctxMenu.x} y={ctxMenu.y}
          data={data}
          panelTitle={config.title}
          darkMode={darkMode}
          onShowTable={() => setShowDataOpen(true)}
          onClose={() => setCtxMenu(null)}
        />
      )}
      {showDataOpen && (
        <ShowDataModal
          data={data}
          panelTitle={config.title}
          darkMode={darkMode}
          onClose={() => setShowDataOpen(false)}
        />
      )}
      {renderChart()}

      {/* Header overlay */}
      <div style={{
        position: 'absolute',
        top: '6px',
        left: '6px',
        right: '140px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '6px',
        flexWrap: 'nowrap',
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 10
      }}>
        <div className="panel-drag-handle" style={{
          fontWeight: '700',
          color: theme.text,
          fontSize: isSmall ? '11px' : '13px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          textShadow: darkMode
            ? '1px 1px 3px rgba(0,0,0,0.9), -1px -1px 3px rgba(0,0,0,0.9)'
            : '1px 1px 2px rgba(255,255,255,1), -1px -1px 2px rgba(255,255,255,1)',
          pointerEvents: 'auto',
          cursor: 'grab',
          flexShrink: 0
        }}>
          <div style={{
            width: '5px',
            height: '5px',
            borderRadius: '50%',
            background: getColor(0),
            boxShadow: `0 0 8px ${getColor(0)}`,
            animation: 'pulse 2s infinite',
            flexShrink: 0
          }} />
          <span style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '200px'
          }}>
            {config.title}
          </span>
        </div>

        {/* Cross-filter chips — inline with the title so they never occlude
            the chart. Same pill style as the config badges below. */}
        {hasCrossFilters && (
          <div style={{
            display: 'flex', gap: '3px', alignItems: 'center', flexWrap: 'wrap',
            flexShrink: 1, minWidth: 0, pointerEvents: 'auto'
          }}>
            {crossFilters.map((cf, i) => (
              <div key={i} title={`${cf.column} ${cf.operator || '='} ${cf.value}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  padding: '2px 6px', borderRadius: 6,
                  background: 'rgba(139, 92, 246, 0.30)',
                  backdropFilter: 'blur(4px)',
                  border: '1px solid rgba(139, 92, 246, 0.6)',
                  color: darkMode ? '#ddd6fe' : '#5b21b6',
                  fontSize: '8px', fontWeight: 700,
                  boxShadow: darkMode ? '0 1px 3px rgba(0,0,0,0.4)' : '0 1px 2px rgba(0,0,0,0.15)',
                  maxWidth: 180, overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}>
                <Filter size={7} />
                {cf.column} = {String(cf.value)}
              </div>
            ))}
          </div>
        )}

        {(config.timeRange || config.filters?.length > 0 || (config.yAxisScale && config.yAxisScale !== 'auto')) && (
          <div style={{
            display: 'flex',
            gap: '3px',
            alignItems: 'center',
            flexShrink: 0,
            pointerEvents: 'auto'
          }}>
            {config.timeRange === 'last' && config.timeRangeLast && (
              <div style={{
                padding: '2px 6px',
                background: darkMode ? 'rgba(102, 126, 234, 0.35)' : 'rgba(102, 126, 234, 0.3)',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(102, 126, 234, 0.6)',
                borderRadius: '6px',
                fontSize: '8px',
                color: darkMode ? '#c7d2fe' : '#4f46e5',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '2px',
                boxShadow: darkMode ? '0 1px 3px rgba(0,0,0,0.4)' : '0 1px 2px rgba(0,0,0,0.15)',
                textShadow: darkMode ? '0 1px 2px rgba(0,0,0,0.5)' : 'none'
              }}>
                <Clock size={7} />
                {config.timeRangeLast}
              </div>
            )}
            {config.timeRange === 'custom' && config.timeRangeStart && (
              <div style={{
                padding: '2px 6px',
                background: darkMode ? 'rgba(102, 126, 234, 0.35)' : 'rgba(102, 126, 234, 0.3)',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(102, 126, 234, 0.6)',
                borderRadius: '6px',
                fontSize: '8px',
                color: darkMode ? '#c7d2fe' : '#4f46e5',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '2px',
                boxShadow: darkMode ? '0 1px 3px rgba(0,0,0,0.4)' : '0 1px 2px rgba(0,0,0,0.15)',
                textShadow: darkMode ? '0 1px 2px rgba(0,0,0,0.5)' : 'none'
              }}>
                <Clock size={7} />
                Custom
              </div>
            )}
            {config.filters && config.filters.length > 0 && (
              <div style={{
                padding: '2px 6px',
                background: darkMode ? 'rgba(16, 185, 129, 0.35)' : 'rgba(16, 185, 129, 0.3)',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(16, 185, 129, 0.6)',
                borderRadius: '6px',
                fontSize: '8px',
                color: darkMode ? '#a7f3d0' : '#047857',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '2px',
                boxShadow: darkMode ? '0 1px 3px rgba(0,0,0,0.4)' : '0 1px 2px rgba(0,0,0,0.15)',
                textShadow: darkMode ? '0 1px 2px rgba(0,0,0,0.5)' : 'none'
              }}>
                <Filter size={7} />
                {config.filters.length}
              </div>
            )}
            {config.yAxisScale && config.yAxisScale !== 'auto' && (
              <div style={{
                padding: '2px 6px',
                background: darkMode ? 'rgba(245, 158, 11, 0.35)' : 'rgba(245, 158, 11, 0.3)',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(245, 158, 11, 0.6)',
                borderRadius: '6px',
                fontSize: '8px',
                color: darkMode ? '#fde68a' : '#d97706',
                fontWeight: '700',
                textTransform: 'capitalize',
                boxShadow: darkMode ? '0 1px 3px rgba(0,0,0,0.4)' : '0 1px 2px rgba(0,0,0,0.15)',
                textShadow: darkMode ? '0 1px 2px rgba(0,0,0,0.5)' : 'none'
              }}>
                {config.yAxisScale}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div style={{
        position: 'absolute',
        top: '6px',
        right: '6px',
        display: 'flex',
        gap: '3px',
        zIndex: 10
      }}>
        <button onClick={() => fetchData()} style={{
          padding: isSmall ? '5px' : '6px',
          background: 'transparent',
          border: 'none',
          color: theme.text,
          cursor: 'pointer',
          borderRadius: '6px',
          transition: 'all 0.2s ease',
          textShadow: darkMode ? '1px 1px 3px rgba(0,0,0,0.9)' : '1px 1px 2px rgba(255,255,255,1)'
        }} 
        onMouseEnter={(e) => {
          e.currentTarget.style.background = darkMode ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
        title="Refresh">
          <RefreshCw size={isSmall ? 12 : 13} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
        </button>
        <button onClick={() => setFocused(f => !f)} style={{
          padding: isSmall ? '5px' : '6px',
          background: 'transparent',
          border: 'none',
          color: theme.text,
          cursor: 'pointer',
          borderRadius: '6px',
          transition: 'all 0.2s ease',
          textShadow: darkMode ? '1px 1px 3px rgba(0,0,0,0.9)' : '1px 1px 2px rgba(255,255,255,1)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = darkMode ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
        title={focused ? 'Exit focus mode (Esc)' : 'Focus mode'}>
          {focused ? <Minimize2 size={isSmall ? 12 : 13} /> : <Maximize2 size={isSmall ? 12 : 13} />}
        </button>
        {/* Mobile: collapse the secondary actions behind a ⋯ menu */}
        {isMobileView && (
          <button onClick={() => setMobileMenuOpen(o => !o)} title="More actions" style={{
            padding: '6px', border: 'none', cursor: 'pointer', borderRadius: '6px', color: theme.text,
            background: mobileMenuOpen ? (darkMode ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.1)') : 'transparent',
            textShadow: darkMode ? '1px 1px 3px rgba(0,0,0,0.9)' : '1px 1px 2px rgba(255,255,255,1)'
          }}>
            <MoreVertical size={16} />
          </button>
        )}
        {/* On desktop this wrapper is display:contents (invisible to layout, so
            the buttons stay inline exactly as before). On mobile it becomes a
            dropdown anchored under the ⋯ button. */}
        <div
          onClick={() => { if (isMobileView) setMobileMenuOpen(false); }}
          style={isMobileView ? {
            display: mobileMenuOpen ? 'flex' : 'none',
            flexDirection: 'column', gap: '2px',
            position: 'absolute', top: 'calc(100% + 4px)', right: 0,
            background: theme.card, border: `1px solid ${theme.border}`,
            borderRadius: '8px', padding: '4px', zIndex: 20,
            boxShadow: '0 8px 24px rgba(0,0,0,0.35)'
          } : { display: 'contents' }}
        >
        {onHidePanel && (
          <button
            onClick={() => onHidePanel(config.id)}
            style={{
              padding: isSmall ? '5px' : '6px',
              background: 'transparent', border: 'none', color: theme.text,
              cursor: 'pointer', borderRadius: '6px', transition: 'all 0.2s ease',
              textShadow: darkMode ? '1px 1px 3px rgba(0,0,0,0.9)' : '1px 1px 2px rgba(255,255,255,1)'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = darkMode ? 'rgba(139,92,246,0.20)' : 'rgba(124,58,237,0.12)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            title="Hide from view (save with a bookmark for spotlight mode)"
          >
            <EyeOff size={isSmall ? 12 : 13} />
          </button>
        )}
        {config.drillThroughTargetId && (
          <button
            onClick={() => {
              // Miralys manages dashboards in local state (no URL routes),
              // so we hand the target dashboard id + filter payload to the
              // parent Dashboard via a custom window event. The receiver
              // switches the current dashboard; the filter bus's provider
              // (keyed by dashboard id) remounts, and DrillFilterSeeder
              // reads the payload from sessionStorage.
              const targetId = config.drillThroughTargetId;
              // Include the ENTIRE dashboard's active filter set — not just
              // what THIS panel receives. That means a bar click on the same
              // panel carries over on drill-through too, not only clicks from
              // other panels. Falls back to crossFilters if the wrapper
              // didn't supply allActiveFilters.
              const source = (allActiveFilters && allActiveFilters.length)
                ? allActiveFilters
                : (crossFilters || []);
              const payload = source
                .filter(cf => cf?.column && cf?.value !== undefined && cf?.value !== '')
                .map((cf, i) => ({
                  sourceId: `drill_${i}`,
                  column: cf.column, value: cf.value, operator: cf.operator || '='
                }));
              try {
                sessionStorage.setItem(
                  `miralys.drillFilter.${targetId}`,
                  JSON.stringify(payload)
                );
              } catch { /* private mode / disabled storage — non-fatal */ }
              window.dispatchEvent(new CustomEvent('miralys:drillThrough', {
                detail: { targetId, filters: payload, fromPanelTitle: config.title }
              }));
            }}
            style={{
              padding: isSmall ? '5px' : '6px',
              background: 'transparent', border: 'none', color: theme.text,
              cursor: 'pointer', borderRadius: '6px', transition: 'all 0.2s ease',
              textShadow: darkMode ? '1px 1px 3px rgba(0,0,0,0.9)' : '1px 1px 2px rgba(255,255,255,1)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = darkMode ? 'rgba(139, 92, 246, 0.22)' : 'rgba(139, 92, 246, 0.12)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
            title={config.drillThroughLabel
              ? `Drill through: ${config.drillThroughLabel}`
              : 'Drill through to detail dashboard'}
          >
            <ArrowUpRight size={isSmall ? 12 : 13} />
          </button>
        )}
        {!readOnly && onDuplicate && (
        <button onClick={() => onDuplicate(config)} style={{
          padding: isSmall ? '5px' : '6px',
          background: 'transparent',
          border: 'none',
          color: theme.text,
          cursor: 'pointer',
          borderRadius: '6px',
          transition: 'all 0.2s ease',
          textShadow: darkMode ? '1px 1px 3px rgba(0,0,0,0.9)' : '1px 1px 2px rgba(255,255,255,1)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = darkMode ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
        title="Duplicate">
          <Copy size={isSmall ? 11 : 12} />
        </button>
        )}
        {!readOnly && onEdit && (
        <button onClick={() => onEdit(config)} style={{
          padding: isSmall ? '5px' : '6px',
          background: 'transparent',
          border: 'none',
          color: theme.text,
          cursor: 'pointer',
          borderRadius: '6px',
          transition: 'all 0.2s ease',
          textShadow: darkMode ? '1px 1px 3px rgba(0,0,0,0.9)' : '1px 1px 2px rgba(255,255,255,1)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = darkMode ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.1)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
        title="Edit">
          <Settings size={isSmall ? 11 : 12} />
        </button>
        )}
        {!readOnly && onDelete && (
        <button onClick={() => onDelete(config.id)} style={{
          padding: isSmall ? '5px' : '6px',
          background: 'transparent',
          border: 'none',
          color: '#ef4444',
          cursor: 'pointer',
          borderRadius: '6px',
          transition: 'all 0.2s ease',
          textShadow: darkMode ? '1px 1px 3px rgba(0,0,0,0.9)' : '1px 1px 2px rgba(255,255,255,1)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)';
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
        title="Delete">
          <Trash2 size={isSmall ? 11 : 12} />
        </button>
        )}
        </div>
      </div>

      {/* Footer info */}
      <div style={{
        position: 'absolute',
        bottom: '6px',
        left: '6px',
        right: '6px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pointerEvents: 'none',
        zIndex: 10
      }}>
        <div style={{
          fontSize: isSmall ? '9px' : '11px',
          color: error ? '#ef4444' : (offline ? '#f59e0b' : theme.textMuted),
          display: 'flex',
          gap: isSmall ? '4px' : '6px',
          alignItems: 'center',
          flexWrap: 'wrap',
          fontWeight: '600',
          textShadow: darkMode ? '1px 1px 3px rgba(0,0,0,0.9)' : '1px 1px 2px rgba(255,255,255,1)'
        }}>
          {error ? (
            <>
              <AlertCircle size={isSmall ? 8 : 9} color="#ef4444" />
              <span style={{ color: '#ef4444' }}>Error: {error}</span>
              <div>•</div>
              <span style={{ color: theme.textMuted }}>Showing last data</span>
            </>
          ) : offline ? (
            <>
              <AlertCircle size={isSmall ? 8 : 9} color="#f59e0b" />
              <span style={{ color: '#f59e0b' }}>Agent offline</span>
              <div>•</div>
              <span style={{ color: theme.textMuted }}>Showing last data</span>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
                {loading && <RefreshCw size={isSmall ? 9 : 11} style={{ animation: 'spin 1s linear infinite' }} />}
                {!loading && <Play size={isSmall ? 9 : 11} />}
                <span>{config.refreshInterval > 0 ? `${config.refreshInterval / 1000}s` : 'Manual'}</span>
                {usingWebSocket && <span style={{ color: '#10b981', fontWeight: 700 }}>WS</span>}
              </div>
              <div>•</div>
              <div style={{ textTransform: 'capitalize' }}>{config.vizType}</div>
              <div>•</div>
              <div>{data.length} pts</div>
              <div>•</div>
              <div>TZ: {config.timezone || 'UTC'}</div>
              {!isSmall && !isMobileView && (
                <>
                  <div>•</div>
                  <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
                    <Clock size={10} />
                    <span title="Query time">Q: {formatTimestamp(queryTime)}</span>
                  </div>
                  <div>•</div>
                  <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
                    <Database size={10} />
                    <span title="Latest record time">L: {formatTimestamp(latestRecordTime)}</span>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        /* Force Recharts tick colors */
        .recharts-cartesian-axis-tick text {
          fill: ${theme.chartText} !important;
        }
        
        .recharts-label text {
          fill: ${theme.text} !important;
        }
        
        .recharts-cartesian-axis-line {
          stroke: ${theme.chartAxis} !important;
        }
        
        .recharts-cartesian-axis-tick-line {
          stroke: ${theme.chartAxis} !important;
        }
        
        .recharts-polar-angle-axis-tick text,
        .recharts-polar-radius-axis-tick text {
          fill: ${theme.chartText} !important;
        }
      `}</style>
    </div>
  );

  // Focus mode: portal the same panel into a fullscreen overlay. The inner
  // markup is identical — Recharts' ResponsiveContainer resizes automatically.
  if (focused) {
    return createPortal(
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9500,
        background: darkMode ? 'rgba(6, 10, 20, 0.96)' : 'rgba(240, 244, 255, 0.96)',
        padding: 24, display: 'flex', flexDirection: 'column'
      }}
      onKeyDown={(e) => { if (e.key === 'Escape') setFocused(false); }}
      tabIndex={-1}
      >
        <div style={{ flex: 1, minHeight: 0 }}>{panelInner}</div>
      </div>,
      document.body
    );
  }

  return panelInner;
}

export default QuestDBPanel;
